<template>
  <div class="update-surat-masuk-page">
    <div class="page-header">
      <div class="header-title-wrapper">
        <h1>Update Surat Masuk</h1>
        <div class="status-surat">
          <a-tag
            v-if="formData.status === 'Didisposisikan'"
            color="orange"
            style="border-radius: 50px;"
          >
            <strong>{{ formData.status }}</strong>
          </a-tag>
          <a-tag v-else-if="formData.status === 'Selesai Disposisi'" color="green">
            <strong>{{ formData.status }}</strong>
          </a-tag>
          <a-tag v-else-if="formData.status === 'Diarsipkan'" color="blue">
            <strong>{{ formData.status }}</strong>
          </a-tag>
          <a-tag v-else-if="formData.status" color="default">
            <strong>{{ formData.status }}</strong>
          </a-tag>

          <a-tag
            v-if="
              !commentsStatusLoading &&
                formData.status === 'Didisposisikan' &&
                formData.disposisi === 'pengurus' &&
                displayCommentsStatus()
            "
            :color="getCommentsStatusColor()"
          >
            <strong>{{ getCommentsStatus() }}</strong>
          </a-tag>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <a-spin size="large" tip="Memuat data surat masuk..." />
    </div>

    <!-- Main Content -->
    <div v-else-if="suratData" class="content-container">
      <h4 class="section-title">Detail Surat</h4>
      <a-form :model="formData" layout="vertical" ref="formRef" @keydown.enter.prevent>
        <!-- Top Info Fields -->
        <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
          <a-row :gutter="16">
            <a-col :span="6">
              <span style="font-weight: 500;">Pengarsip Surat</span>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <span>{{ formData.pengarsipSurat }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16">
            <a-col :span="6">
              <span style="font-weight: 500;">Waktu Diregistrasikan</span>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <span>{{ formData.waktuDiregistrasikan }}</span>
            </a-col>
          </a-row>
        </div>

        <!-- File Upload/Display Section -->
        <a-form-item
          :validate-status="validationErrors.dokumen ? 'error' : ''"
          :help="validationErrors.dokumen"
        >
          <template #label>
            <span>Dokumen Surat <span style="color: #ff4d4f;">*</span></span>
          </template>
          <!-- Show upload area if file is deleted -->
          <div
            v-if="!formData.pathToFile"
            class="upload-area"
            @click="triggerFileInput"
            @drop.prevent="handleFileDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @dragenter.prevent
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              style="display: none"
              @change="handleFileChange"
            />
            <div class="upload-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
              >
                <path
                  d="M35.0457 14.6766L35.0363 14.6391L29.7769 1.275C29.5425 0.520312 28.8441 0 28.0519 0H6.73316C5.93629 0 5.22848 0.529688 5.00348 1.29375L0.0862889 14.5219L0.072227 14.5547L0.0628514 14.5922C0.00191392 14.8219 -0.0168361 15.0562 0.0159764 15.2859C0.0112889 15.3609 0.00660156 15.4359 0.00660156 15.5109V32.6625C0.00784108 33.418 0.308506 34.1422 0.842716 34.6764C1.37693 35.2106 2.10112 35.5113 2.8566 35.5125H32.2566C33.8269 35.5125 35.1066 34.2328 35.1113 32.6625V15.5109C35.1113 15.45 35.1113 15.3891 35.1066 15.3375C35.1254 15.1078 35.1066 14.8875 35.0457 14.6766ZM21.18 12.6609L21.166 13.3969C21.1285 15.5016 19.6754 16.9172 17.5519 16.9172C16.516 16.9172 15.6254 16.5844 14.9832 15.9516C14.341 15.3187 13.9894 14.4375 13.9707 13.3969L13.9566 12.6609H4.30973L8.03629 3.6H26.7488L30.5785 12.6609H21.18ZM3.60191 16.2609H10.9754C12.1144 18.9375 14.5379 20.5172 17.5566 20.5172C19.1363 20.5172 20.6035 20.0766 21.7894 19.2422C22.83 18.5109 23.641 17.4891 24.166 16.2609H31.5019V31.9125H3.60191V16.2609Z"
                  fill="#E10F28"
                />
              </svg>
            </div>
            <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
            <p class="upload-hint">Unggah file dalam format PDF, PNG, atau JPG/JPEG</p>
          </div>

          <!-- Show file info with delete button if file exists -->
          <div v-else class="file-attachment">
            <PaperClipOutlined class="file-icon" />
            <span @click="openFilePreview" class="file-name file-link" style="cursor: pointer;">
              {{ getFileName(formData.pathToFile) }}
            </span>
            <button
              type="button"
              class="delete-button"
              @click.prevent="deleteFile"
              title="Hapus file"
              v-if="!isFieldsDisabled"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        </a-form-item>

        <!-- Auto-detect button - show when file is replaced -->
        <a-form-item v-if="newFile">
          <div class="auto-detect-wrapper">
            <a-button
              class="auto-detect-button"
              @click="autoDetectWithAI"
              :disabled="!newFile || isDetecting"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <mask
                  id="mask0_1239_4989"
                  style="mask-type: alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                >
                  <rect width="20" height="20" fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_1239_4989)">
                  <path
                    d="M4.0625 17.021L3 15.9585L9.08333 9.87516L5.5 9.00016L9.375 6.5835L9.04167 2.021L12.5417 4.9585L16.75 3.25016L15.0417 7.47933L18 10.9585L13.4381 10.6252L11.0208 14.521L10.1458 10.9377L4.0625 17.021ZM4.5 7.00016L3 5.50016L4.5 4.00016L6 5.50016L4.5 7.00016ZM11.6042 10.6877L12.625 9.06266L14.5417 9.2085L13.306 7.746L14.0269 5.9585L12.2556 6.68766L10.7917 5.4585L10.9375 7.37516L9.29167 8.38329L11.1531 8.836L11.6042 10.6877ZM14.5 17.0002L13 15.5002L14.5 14.0002L16 15.5002L14.5 17.0002Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
              {{ isDetecting ? 'Mendeteksi' : 'AUTO-DETEKSI ISI SURAT' }}
            </a-button>
            <a-tooltip placement="right">
              <template #title>
                <div style="max-width: 300px">
                  Fitur Auto-Deteksi dengan AI dapat mengisi field Nomor Surat, Tanggal Surat,
                  Pengirim, dan Perihal secara otomatis. Mohon upload file surat dalam format PDF
                  atau scan surat fisik sejauh mungkin.
                </div>
              </template>
              <question-circle-outlined class="info-icon" />
            </a-tooltip>
          </div>
        </a-form-item>

        <div v-if="isDetecting">
          <a-divider />
          <div class="loading-modal">
            <div class="loading-spinner"></div>
            <h3 class="loading-title">Mohon tunggu</h3>
            <p class="loading-text">
              Sistem sedang membaca isi surat dan mengisi formulir registrasi surat masuk
            </p>
          </div>
        </div>

        <div v-else>
          <a-row :gutter="16">
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.waktuPenerimaan ? 'error' : ''"
                :help="validationErrors.waktuPenerimaan"
              >
                <template #label>
                  <span>Waktu Penerimaan Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-date-picker
                  v-model:value="formData.waktuPenerimaan"
                  placeholder="DD-MM-YYYY HH:mm"
                  size="large"
                  style="width: 100%"
                  format="DD-MM-YYYY HH:mm"
                  show-time
                  :disabled="isFieldsDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.nomorSurat ? 'error' : ''"
                :help="validationErrors.nomorSurat"
              >
                <template #label>
                  <span>Nomor Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-input
                  v-model:value="formData.nomorSurat"
                  placeholder="Nomor surat"
                  size="large"
                  :disabled="isFieldsDisabled"
                  @keypress.space.prevent
                  @input="formData.nomorSurat = formData.nomorSurat.replace(/\s+/g, '')"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="16">
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.tanggalSurat ? 'error' : ''"
                :help="validationErrors.tanggalSurat"
              >
                <template #label>
                  <span>Tanggal Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-date-picker
                  v-model:value="formData.tanggalSurat"
                  placeholder="DD-MM-YYYY"
                  size="large"
                  style="width: 100%"
                  format="DD-MM-YYYY"
                  :disabled="isFieldsDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.pengirim ? 'error' : ''"
                :help="validationErrors.pengirim"
              >
                <template #label>
                  <span>Pengirim <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-input
                  v-model:value="formData.pengirim"
                  placeholder="Orang atau lembaga pengirim surat"
                  size="large"
                  :disabled="isFieldsDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="16">
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.perihal ? 'error' : ''"
                :help="validationErrors.perihal"
              >
                <template #label>
                  <span>Perihal <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-textarea
                  v-model:value="formData.perihal"
                  placeholder="Perihal surat"
                  :rows="4"
                  size="large"
                  :disabled="isFieldsDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item label="Keterangan">
                <a-textarea
                  v-model:value="formData.keterangan"
                  placeholder="Keterangan atau catatan tambahan terkait surat"
                  :rows="4"
                  size="large"
                  :disabled="isFieldsDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </div>

        <a-divider style="margin-top: 12px;" />

        <h4 class="section-title">Disposisi</h4>

        <a-form-item
          style="margin-bottom: 12px"
          :validate-status="validationErrors.disposisi ? 'error' : ''"
          :help="validationErrors.disposisi"
        >
          <template #label>
            <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
          </template>
          <a-radio-group
            v-model:value="formData.disposisi"
            size="large"
            :disabled="formData.previousDisposisiType !== 'tidak didisposisi' || isFieldsDisabled"
          >
            <a-radio value="tidak didisposisi">Tidak Didisposisi</a-radio>
            <a-radio value="pekerja harian">Pekerja Harian</a-radio>
            <a-radio value="pengurus">Pengurus</a-radio>
            <a-radio value="penugasan dinas">Pengurus (Penugasan Dinas)</a-radio>
          </a-radio-group>
        </a-form-item>

        <!-- Surat Tugas List for Penugasan Dinas -->
        <div
          v-if="formData.disposisi === 'penugasan dinas' && suratData?.suratKeluar"
          class="surat-tugas-list"
        >
          <p class="surat-tugas-info">
            Surat tugas terkait surat ini dapat dilihat dan diubah pada tautan di bawah ini.
          </p>
          <div
            v-for="perwakilan in suratData.suratKeluar.perwakilanDinas"
            :key="perwakilan.id"
            class="surat-tugas-item"
          >
            <PaperClipOutlined class="surat-tugas-icon" />
            <span class="surat-tugas-name">
              {{ getSuratTugasFileName(perwakilan.pdfPath) }}
            </span>
            <span
              @click.stop.prevent="openSuratTugasPreview(perwakilan)"
              class="surat-tugas-action"
              style="cursor: pointer;"
              title="Preview Surat Tugas"
            >
              <EyeOutlined />
            </span>
            <a
              :href="`/surat-keluar/${suratData.suratKeluar.id}`"
              target="_blank"
              class="surat-tugas-action"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>

        <!-- New Penugasan Dinas Form Section - Show only when changing from 'tidak didisposisi' -->
        <div
          v-if="
            formData.disposisi === 'penugasan dinas' &&
              formData.previousDisposisiType === 'tidak didisposisi'
          "
          class="penugasan-dinas-section"
        >
          <p class="penugasan-info-text">
            Mengisi bagian ini akan membuat surat keluar baru berupa surat tugas secara otomatis
            berdasarkan data yang dimasukkan. Surat tugas akan dikirimkan ke verifikator atau
            penandatangan yang dipilih.
          </p>

          <!-- Nomor Surat Section -->
          <div class="nomor-surat-input">
            <label class="section-label">Nomor Surat <span style="color: #ff4d4f">*</span></label>
            <p class="section-description">
              Apabila nomor surat sudah digunakan oleh surat yang dibuat di luar sistem, gunakan
              tombol tambah (+) untuk menaikkan nomor surat
            </p>
            <div class="input-wrapper">
              <a-input
                v-model:value="formData.penugasanDinas.nomorSurat"
                placeholder="Nomor surat tugas"
                disabled
                size="large"
                class="nomor-input"
              />
              <a-button type="text" class="btn-action" @click.prevent="incrementNomorSurat">
                <PlusOutlined />
              </a-button>
              <a-button
                type="text"
                class="btn-action"
                @click.prevent="decrementNomorSurat"
                :disabled="!nomorSuratState.canDecrement"
              >
                <MinusOutlined />
              </a-button>
            </div>
            <span v-if="validationErrors.pd_nomorSurat" class="error-hint">
              {{ validationErrors.pd_nomorSurat }}
            </span>
          </div>

          <!-- Sifat Surat Section -->
          <a-form-item
            :validate-status="validationErrors.pd_sifatSurat ? 'error' : ''"
            :help="validationErrors.pd_sifatSurat"
          >
            <template #label>
              <span>Sifat Surat <span style="color: #ff4d4f;">*</span></span>
            </template>
            <a-radio-group v-model:value="formData.penugasanDinas.sifatSurat" size="large">
              <a-radio value="biasa">Biasa</a-radio>
              <a-radio value="terbatas">Terbatas</a-radio>
              <a-radio value="rahasia">Rahasia</a-radio>
              <a-radio value="sangat rahasia">Sangat Rahasia</a-radio>
            </a-radio-group>
          </a-form-item>

          <!-- Bentuk Surat Section -->
          <a-form-item
            :validate-status="validationErrors.pd_bentukSurat ? 'error' : ''"
            :help="validationErrors.pd_bentukSurat"
          >
            <template #label>
              <span>Bentuk Surat <span style="color: #ff4d4f;">*</span></span>
            </template>
            <a-radio-group v-model:value="formData.penugasanDinas.bentukSurat">
              <div class="bentuk-surat-option">
                <a-radio value="Soft copy">Soft copy</a-radio>
                <p class="bentuk-surat-desc">
                  Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website ini
                  (format PDF).
                </p>
              </div>
              <div class="bentuk-surat-option">
                <a-radio value="Hard copy">Hard copy</a-radio>
                <p class="bentuk-surat-desc">
                  Menggunakan tanda tangan dan stempel basah. Surat dapat diambil di Sekretariat
                  PERKI.
                </p>
              </div>
            </a-radio-group>
          </a-form-item>

          <!-- Main Details Row -->
          <a-row :gutter="16">
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.pd_namaKegiatan ? 'error' : ''"
                :help="validationErrors.pd_namaKegiatan"
              >
                <template #label>
                  <span>Nama Kegiatan <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-input
                  v-model:value="formData.penugasanDinas.namaKegiatan"
                  placeholder="Nama kegiatan dinas"
                  size="large"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.pd_waktuPerjalanan ? 'error' : ''"
                :help="validationErrors.pd_waktuPerjalanan"
              >
                <template #label>
                  <span>Waktu Perjalanan <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-date-picker
                  v-model:value="formData.penugasanDinas.waktuPerjalanan"
                  placeholder="DD-MM-YYYY HH:MM"
                  size="large"
                  style="width: 100%"
                  format="DD-MM-YYYY HH:mm"
                  show-time
                />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- Location and Date Row -->
          <a-row :gutter="16">
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.pd_tempatKegiatan ? 'error' : ''"
                :help="validationErrors.pd_tempatKegiatan"
              >
                <template #label>
                  <span>Tempat Kegiatan <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-input
                  v-model:value="formData.penugasanDinas.tempatKegiatan"
                  placeholder="Tempat kegiatan dinas"
                  size="large"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="12">
              <a-form-item
                :validate-status="validationErrors.pd_tanggalSurat ? 'error' : ''"
                :help="validationErrors.pd_tanggalSurat"
              >
                <template #label>
                  <span>Tanggal Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-date-picker
                  v-model:value="formData.penugasanDinas.tanggalSurat"
                  placeholder="DD-MM-YYYY"
                  size="large"
                  style="width: 100%"
                  format="DD-MM-YYYY"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- Penandatangan Section -->
          <PenandatanganSelect
            v-model:kiri-value="formData.penugasanDinas.penandatanganKiriId"
            v-model:kanan-value="formData.penugasanDinas.penandatanganKananId"
            :kiri-error="validationErrors.pd_penandatanganKiri"
            :kanan-error="validationErrors.pd_penandatanganKanan"
          />

          <!-- Keperluan Section -->
          <a-form-item
            :validate-status="validationErrors.pd_keperluan ? 'error' : ''"
            :help="validationErrors.pd_keperluan"
          >
            <template #label>
              <span>Keperluan <span style="color: #ff4d4f;">*</span></span>
            </template>
            <a-textarea
              v-model:value="formData.penugasanDinas.keperluan"
              placeholder="Keperluan dinas"
              :rows="3"
              size="large"
            />
          </a-form-item>

          <!-- Perwakilan Dinas Table -->
          <PerwakilanDinasTable
            :data-source="formData.penugasanDinas.perwakilan"
            :show-pdf-button="false"
            :error="validationErrors.pd_perwakilan"
            @add="addPerwakilanDinas"
            @remove="removePerwakilanDinas"
            @preview="handlePreviewPerwakilan"
          />

          <!-- Verifikator Section -->
          <VerifikatorTable
            :data-source="formData.penugasanDinas.verifikator"
            @add="addVerifikator"
            @remove="removeVerifikator"
          />

          <AlurVerifikasi :verifikator="formData.penugasanDinas.verifikator" />
        </div>

        <p
          v-if="formData.disposisi === 'pengurus' && existingPengurusDispositions.length > 0"
          class="disposisi-info-text"
        >
          Disposisi yang sudah dibuat tidak dapat diubah atau dihapus. Apabila terdapat kesalahan
          instruksi, Anda dapat menginformasikan langsung ke tujuan disposisi melalui fitur komentar
          disposisi.
        </p>

        <p
          v-if="formData.disposisi === 'pengurus' && existingPengurusDispositions.length === 0"
          class="disposisi-info-text"
        >
          Mengisi bagian ini akan membuat disposisi baru untuk pengurus yang dituju.
        </p>

        <p
          v-if="formData.disposisi === 'pekerja harian' && existingTasks.length > 0"
          class="disposisi-info-text"
        >
          Task yang sudah dibuat hanya dapat diubah dan dihapus melalui menu Tasks pada aplikasi
          Lark. Namun, Anda tetap dapat menambahkan task baru pada aplikasi Lark secara otomatis
          melalui bagian ini.
        </p>

        <p
          v-if="formData.disposisi === 'pekerja harian' && existingTasks.length === 0"
          class="disposisi-info-text"
        >
          Mengisi bagian ini akan membuat task baru pada aplikasi Lark secara otomatis berdasarkan
          data yang dimasukkan.
        </p>

        <!-- Existing Disposisi Cards for Pekerja Harian - Display as disabled cards -->
        <div v-if="formData.disposisi === 'pekerja harian'" class="task-cards-section">
          <!-- Existing Task Cards (Read-only with disabled fields) -->
          <div
            v-for="(task, taskIndex) in existingTasks"
            :key="'existing-task-' + taskIndex"
            class="task-card"
          >
            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item>
                  <template #label>
                    <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-select
                    :value="task.disposisiKepadaUser?.map(u => u.id) || []"
                    placeholder="Pekerja harian"
                    mode="multiple"
                    size="large"
                    :options="pekerjaHarianOptions"
                    disabled
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item>
                  <template #label>
                    <span>Judul Task <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-input :value="task.judulTask" placeholder="Judul Task" size="large" disabled />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item label="Deadline Task">
                  <a-date-picker
                    :value="task.deadlineTask"
                    placeholder="DD-MM-YYYY"
                    size="large"
                    style="width: 100%"
                    format="DD-MM-YYYY"
                    disabled
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item label="Deskripsi Task">
                  <a-textarea
                    :value="task.deskripsiTask"
                    placeholder="Deskripsi task"
                    :rows="4"
                    size="large"
                    disabled
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </div>

          <!-- Add New Task Form -->
          <div
            v-for="(task, taskIndex) in newTasks"
            :key="'new-task-' + taskIndex"
            class="task-card"
          >
            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[`task_${taskIndex}_disposisi`] ? 'error' : ''"
                  :help="validationErrors[`task_${taskIndex}_disposisi`]"
                >
                  <template #label>
                    <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-select
                    v-model:value="task.disposisi"
                    placeholder="Pilih satu atau lebih pekerja harian"
                    mode="multiple"
                    size="large"
                    show-search
                    :options="pekerjaHarianOptions"
                    :filter-option="filterOption"
                    :suffix-icon="() => null"
                    :show-arrow="true"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[`task_${taskIndex}_judul`] ? 'error' : ''"
                  :help="validationErrors[`task_${taskIndex}_judul`]"
                >
                  <template #label>
                    <span>Judul Task <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-input
                    v-model:value="task.judul"
                    placeholder="Proposal Acara XXX"
                    size="large"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item label="Deadline Task">
                  <a-date-picker
                    v-model:value="task.deadline"
                    placeholder="DD-MM-YYYY"
                    size="large"
                    style="width: 100%"
                    format="DD-MM-YYYY"
                    :disabled-date="disabledPastDates"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item label="Deskripsi Task">
                  <a-textarea
                    v-model:value="task.deskripsi"
                    placeholder="Mohon tindaklanjuti surat ini dengan membuat dokumen proposal."
                    :rows="4"
                    size="large"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-form-item>
              <button
                class="hapus-task-disposisi remove-cancel-primary"
                @click.prevent="removeNewTask(taskIndex)"
              >
                <DeleteOutlined />
                HAPUS TASK DISPOSISI
              </button>
            </a-form-item>
          </div>

          <a-button
            v-if="!isFieldsDisabled"
            type="link"
            class="tambah-task-button"
            @click="addNewTask"
          >
            <plus-outlined />
            TAMBAH TASK DISPOSISI
          </a-button>
        </div>

        <!-- Existing Disposisi Cards for Pengurus -->
        <div v-if="formData.disposisi === 'pengurus'" class="disposisi-cards-section">
          <!-- Existing Pengurus Cards (Read-only) -->
          <div
            v-for="(pengurus, pengurusIndex) in existingPengurusDispositions"
            :key="'existing-pengurus-' + pengurusIndex"
            class="disposisi-item-card"
          >
            <div class="disposisi-item-header">
              <div class="disposisi-item-left">
                <div class="disposisi-item-title">
                  Ke {{ getDisposisiKepadaNames(pengurus.disposisiKepadaUser) }}
                </div>
                <div class="disposisi-item-subtitle">Perihal: {{ pengurus.perihalInstruksi }}</div>
                <div class="disposisi-item-instruksi">
                  Instruksi: {{ pengurus.instruksiDisposisi || 'Tidak ada' }}
                </div>
              </div>
              <div class="disposisi-item-right">
                <div class="disposisi-item-info">
                  Dari {{ suratData?.creator?.name }} ({{ suratData?.creator?.jabatan }})
                </div>
                <div class="disposisi-item-info">
                  {{ formatDateTime(pengurus.createdAt) }}
                </div>
              </div>
            </div>

            <div class="disposisi-item-actions">
              <div class="comment-tags-small">
                <a-tag :color="getStatusColor(pengurus.statusDisposisi)">{{
                  pengurus.statusDisposisi
                }}</a-tag>
                <a-tag
                  v-if="
                    pengurus.statusDisposisi !== 'Selesai Disposisi' &&
                      isDisposisiCreator(pengurus) &&
                      getDisposisiSubstatus(pengurus.id, 'pengurus').label === 'Dijawab'
                  "
                  :color="getDisposisiSubstatus(pengurus.id, 'pengurus').color"
                >
                  {{ getDisposisiSubstatus(pengurus.id, 'pengurus').label }}
                </a-tag>
              </div>
              <a-button
                type="default"
                class="lihat-komentar-btn"
                :class="{ active: expandedDisposisiId === pengurus.id }"
                @click="toggleDisposisiComments(pengurus.id, 'pengurus')"
              >
                <RightOutlined :rotate="expandedDisposisiId === pengurus.id ? 90 : 0" />
                LIHAT KOMENTAR DISPOSISI
              </a-button>
              <div class="comment-tags-big">
                <a-tag :color="getStatusColor(pengurus.statusDisposisi)">{{
                  pengurus.statusDisposisi
                }}</a-tag>
                <a-tag
                  v-if="
                    pengurus.statusDisposisi !== 'Selesai Disposisi' &&
                      isDisposisiCreator(pengurus) &&
                      getDisposisiSubstatus(pengurus.id, 'pengurus').label === 'Dijawab'
                  "
                  :color="getDisposisiSubstatus(pengurus.id, 'pengurus').color"
                >
                  {{ getDisposisiSubstatus(pengurus.id, 'pengurus').label }}
                </a-tag>
              </div>
            </div>

            <!-- Expanded Comment Section -->
            <div v-if="expandedDisposisiId === pengurus.id" class="disposisi-comments-section">
              <!-- Comments List -->
              <div v-if="disposisiComments.length === 0 && !commentsLoading" class="no-comments">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <mask
                    id="mask0_1350_27690"
                    style="mask-type:alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="40"
                    height="40"
                  >
                    <rect width="40" height="40" fill="#798892" />
                  </mask>
                  <g mask="url(#mask0_1350_27690)">
                    <path
                      d="M10.0007 23.3335H30.0007V20.5556H10.0007V23.3335ZM10.0007 18.0556H30.0007V15.2781H10.0007V18.0556ZM10.0007 12.7781H30.0007V10.0002H10.0007V12.7781ZM36.6673 36.6668L30.0007 30.0002H6.1119C5.3619 30.0002 4.71135 29.7248 4.16023 29.1739C3.6094 28.6228 3.33398 27.9722 3.33398 27.2222V6.11141C3.33398 5.36141 3.6094 4.71086 4.16023 4.15975C4.71135 3.60891 5.3619 3.3335 6.1119 3.3335H33.8894C34.6394 3.3335 35.29 3.60891 35.8411 4.15975C36.3919 4.71086 36.6673 5.36141 36.6673 6.11141V36.6668ZM6.1119 27.2222H31.1952L33.8894 30.0697V6.11141H6.1119V27.2222Z"
                      fill="#798892"
                    />
                  </g>
                </svg>
                <p>
                  Pengurus tujuan disposisi belum melaporkan hasil disposisinya. Anda dapat
                  menambahkan komentar atau instruksi tambahan untuk pengurus tujuan disposisi
                  melalui fitur ini
                </p>
              </div>
              <div v-else class="comments-list">
                <div
                  v-for="comment in disposisiComments"
                  :key="comment.id"
                  class="comment-item"
                  :class="{ 'own-comment': isCommentOwner(comment) }"
                >
                  <div class="comment-bubble">
                    <div class="comment-user-name">
                      {{ isCommentOwner(comment) ? 'Anda' : comment.user?.name }}
                      <div class="comment-time">
                        {{ formatCommentTime(comment.createdAt) }}
                      </div>
                    </div>
                    <div
                      v-if="comment.attachments && comment.attachments.length > 0"
                      class="comment-attachment"
                    >
                      <div
                        v-for="(attachment, idx) in comment.attachments"
                        :key="idx"
                        style="display: flex; gap: 8px;"
                      >
                        <!-- Surat Keluar attachment -->
                        <template v-if="isSuratKeluarAttachment(attachment)">
                          <div class="attachment-link surat-keluar-attachment">
                            <PaperClipOutlined class="attachment-icon" />
                            {{ attachment.name || 'Surat Keluar' }}
                          </div>
                          <span
                            class="view-icon-link"
                            @click.stop.prevent="navigateToSuratKeluar(attachment)"
                            title="Preview Surat Keluar"
                            style="cursor: pointer;"
                          >
                            <EyeOutlined class="view-icon" />
                          </span>
                          <a
                            :href="`/surat-keluar/${attachment.suratKeluarId}`"
                            target="_blank"
                            class="view-icon-link"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              class="view-icon"
                            >
                              <path
                                d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                              ></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                          </a>
                        </template>
                        <!-- Regular file attachment -->
                        <template v-else>
                          <div class="attachment-link">
                            <PaperClipOutlined class="attachment-icon" />
                            {{ getFileName(attachment) || 'Draft Surat Rekomendasi' }}
                          </div>
                          <a :href="getFileUrl(attachment)" target="_blank">
                            <EyeOutlined class="view-icon" />
                          </a>
                        </template>
                      </div>
                    </div>
                    <div class="comment-content">{{ comment.content }}</div>
                  </div>
                </div>
              </div>

              <!-- Comment Input -->
              <div class="comment-input-section">
                <div class="comment-tabs">
                  <button
                    @click="openSuratKeluarModal"
                    class="comment-action-button"
                    :disabled="
                      isDisposisiCompleted(pengurus.id, 'pengurus') ||
                        !canCommentOnDisposisi(pengurus.id, 'pengurus')
                    "
                  >
                    <SearchOutlined /> PILIH SURAT KELUAR
                  </button>
                  <a-upload
                    :file-list="commentFileList"
                    :before-upload="beforeCommentUpload"
                    :show-upload-list="false"
                    :multiple="true"
                    @change="handleCommentFileChange"
                    :disabled="
                      isDisposisiCompleted(pengurus.id, 'pengurus') ||
                        !canCommentOnDisposisi(pengurus.id, 'pengurus')
                    "
                    class="upload-button-wrapper"
                  >
                    <button
                      class="comment-action-button"
                      :disabled="
                        isDisposisiCompleted(pengurus.id, 'pengurus') ||
                          !canCommentOnDisposisi(pengurus.id, 'pengurus')
                      "
                    >
                      <UploadOutlined /> UNGGAH FILE
                    </button>
                  </a-upload>
                </div>

                <!-- Attachment Preview -->
                <div v-if="commentFileList.length" class="attachment-preview">
                  <div
                    v-for="(file, index) in commentFileList"
                    :key="index"
                    class="attachment-item"
                  >
                    <PaperClipOutlined class="file-icon" />
                    <span class="attachment-name">{{ file.name || file.originFileObj?.name }}</span>
                    <button
                      type="button"
                      class="delete-button"
                      @click.prevent="removeCommentFile(index)"
                      title="Hapus file"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path
                          d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                        ></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="comment-textarea-wrapper">
                  <div class="custom-comment-input-row">
                    <a-textarea
                      v-model:value="newCommentText"
                      placeholder="Ketik komentar..."
                      :rows="2"
                      :disabled="
                        submittingComment ||
                          isDisposisiCompleted(pengurus.id, 'pengurus') ||
                          !canCommentOnDisposisi(pengurus.id, 'pengurus')
                      "
                      class="custom-comment-textarea"
                    />
                    <a-button
                      class="custom-send-btn"
                      :loading="submittingComment"
                      :disabled="
                        (!newCommentText.trim() && !commentFileList.length) ||
                          isDisposisiCompleted(pengurus.id, 'pengurus') ||
                          !canCommentOnDisposisi(pengurus.id, 'pengurus')
                      "
                      @click="submitDisposisiComment"
                    >
                      <ArrowUpOutlined />
                      <span class="send-label">KIRIM</span>
                    </a-button>
                  </div>
                </div>
                <a-button
                  v-if="!isDisposisiCompleted(pengurus.id, 'pengurus')"
                  type="default"
                  block
                  class="mark-complete-btn"
                  :disabled="
                    isDisposisiCompleted(pengurus.id, 'pengurus') ||
                      !canCommentOnDisposisi(pengurus.id, 'pengurus')
                  "
                  @click="markDisposisiComplete(pengurus.id, 'pengurus')"
                >
                  <CheckOutlined /> TANDAI SEBAGAI SELESAI DISPOSISI
                </a-button>
              </div>
            </div>
          </div>

          <!-- Add New Pengurus Disposition Form -->
          <div
            v-for="(pengurus, pengurusIndex) in newPengurusDispositions"
            :key="'new-pengurus-' + pengurusIndex"
            class="pengurus-card"
          >
            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="
                    validationErrors[`pengurus_${pengurusIndex}_disposisi`] ? 'error' : ''
                  "
                  :help="validationErrors[`pengurus_${pengurusIndex}_disposisi`]"
                >
                  <template #label>
                    <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-select
                    v-model:value="pengurus.disposisi"
                    placeholder="Pilih pengurus tujuan disposisi"
                    size="large"
                    show-search
                    :options="pengurusOptions"
                    :filter-option="filterOption"
                    :show-arrow="true"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="
                    validationErrors[`pengurus_${pengurusIndex}_perihal`] ? 'error' : ''
                  "
                  :help="validationErrors[`pengurus_${pengurusIndex}_perihal`]"
                >
                  <template #label>
                    <span>Perihal Instruksi <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-input
                    v-model:value="pengurus.perihal"
                    placeholder="Perihal atau judul singkat instruksi"
                    size="large"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-form-item label="Instruksi Disposisi">
              <a-textarea
                v-model:value="pengurus.instruksi"
                placeholder="Deskripsi hal atau instruksi yang perlu dilakukan oleh pengurus"
                :rows="4"
                size="large"
              />
            </a-form-item>

            <a-form-item>
              <button
                class="hapus-disposisi-button"
                @click.prevent="removeNewPengurusDisposition(pengurusIndex)"
              >
                <DeleteOutlined />
                HAPUS DISPOSISI
              </button>
            </a-form-item>
          </div>

          <a-button
            v-if="!isFieldsDisabled"
            type="link"
            class="tambah-disposisi-button"
            @click="addNewPengurusDisposition"
          >
            <plus-outlined />
            TAMBAH TUJUAN DISPOSISI
          </a-button>
        </div>

        <a-divider />

        <a-form-item>
          <button
            v-if="!isFieldsDisabled"
            type="button"
            @click="handleSubmit"
            class="confirm-button"
            :disabled="submitting"
          >
            <span v-if="submitting" class="button-spinner"></span>
            {{ submitting ? 'MENYIMPAN...' : 'SIMPAN PERUBAHAN' }}
          </button>
        </a-form-item>
      </a-form>
    </div>

    <!-- Error State -->
    <div v-else class="error-container">
      <a-result
        status="error"
        title="Gagal Memuat Data"
        sub-title="Data surat masuk tidak ditemukan atau terjadi kesalahan"
      >
        <template #extra>
          <a-button type="primary" @click="goBack">Kembali</a-button>
        </template>
      </a-result>
    </div>

    <!-- Confirmation Modal for Mark Complete -->
    <a-modal
      v-model:visible="confirmCompleteModalVisible"
      :footer="null"
      :closable="false"
      centered
      width="480px"
      class="confirm-complete-modal"
    >
      <div class="confirm-modal-content">
        <h3 class="confirm-modal-title">Tandai disposisi sebagai selesai?</h3>
        <p class="confirm-modal-description">
          Mohon cek kembali komentar disposisi sebelum mengubah status. Disposisi yang sudah
          ditandai sebagai selesai tidak dapat dibuka kembali. Namun, Anda tetap dapat membuat
          disposisi baru jika perlu.
        </p>
        <div class="confirm-modal-actions">
          <button
            class="remove-cancel-button remove-cancel-secondary"
            @click="cancelMarkComplete"
            style="width: 50%;"
          >
            KEMBALI
          </button>
          <button
            class="confirm-button"
            :loading="markingComplete"
            @click="confirmMarkDisposisiComplete"
          >
            YA, TANDAI SELESAI
          </button>
        </div>
      </div>
    </a-modal>

    <!-- Submit Confirmation Modal -->
    <a-modal
      v-model:visible="submitConfirmModalVisible"
      :footer="null"
      :closable="false"
      centered
      width="480px"
      class="confirm-complete-modal"
    >
      <div class="confirm-modal-content">
        <h3 class="confirm-modal-title">Ubah Data Surat Masuk?</h3>
        <p class="confirm-modal-description">
          Mohon cek kembali perubahan data surat sebelum menyimpan formulir.
        </p>
        <div class="confirm-modal-actions">
          <button
            class="remove-cancel-button remove-cancel-secondary"
            style="width: 50%;"
            @click="submitConfirmModalVisible = false"
          >
            KEMBALI
          </button>
          <button class="confirm-button" :disabled="submitting" @click="confirmSubmit">
            YA, SIMPAN
          </button>
        </div>
      </div>
    </a-modal>

    <!-- Surat Keluar Selection Modal -->
    <a-modal
      v-model:visible="suratKeluarModalVisible"
      title="Pilih surat keluar"
      :footer="null"
      width="800px"
      centered
      class="surat-keluar-modal"
    >
      <p class="modal-subtitle">
        Silakan pilih surat keluar yang ingin dikirimkan pada komentar disposisi
      </p>

      <!-- Search Input -->
      <a-input
        v-model:value="suratKeluarSearch"
        placeholder="Cari nomor surat, jenis surat, atau perihal"
        size="large"
        class="surat-keluar-search"
        allow-clear
      >
        <template #prefix>
          <SearchOutlined style="color: #bfbfbf" />
        </template>
      </a-input>

      <!-- Surat Keluar Table -->
      <div class="surat-keluar-table-container">
        <a-spin :spinning="suratKeluarLoading">
          <a-table
            :data-source="filteredSuratKeluar"
            :columns="suratKeluarColumns"
            :row-selection="{
              selectedRowKeys: selectedSuratKeluar,
              onChange: onSuratKeluarSelectionChange,
            }"
            :pagination="false"
            :scroll="{ x: 820, y: 400 }"
            row-key="id"
            size="small"
            class="surat-keluar-table"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'nomorSurat'">
                <span class="surat-nomor">{{ record.nomorSurat }}</span>
              </template>
              <template v-else-if="column.key === 'tanggalSurat'">
                {{ formatDate(record.tanggalSurat) }}
              </template>
              <template v-else-if="column.key === 'jenisSurat'">
                {{ record.jenisSurat }}
              </template>
              <template v-else-if="column.key === 'perihal'">
                {{ record.namaKegiatan || record.perihal || '-' }}
              </template>
              <template v-else-if="column.key === 'pengajuSurat'">
                {{ record.creator?.name || '-' }}
              </template>
            </template>
          </a-table>
        </a-spin>
      </div>

      <!-- Modal Actions -->
      <div class="surat-keluar-modal-actions">
        <button
          class="confirm-button"
          :disabled="selectedSuratKeluar.length === 0"
          @click="confirmSuratKeluarSelection"
        >
          <ArrowUpOutlined /> LAMPIRKAN SURAT
        </button>
        <button class="remove-cancel-button remove-cancel-secondary" @click="closeSuratKeluarModal">
          KEMBALI
        </button>
      </div>
    </a-modal>

    <!-- Floating Scroll to Top Button -->
    <transition name="fade">
      <button
        v-show="showScrollTop"
        class="scroll-to-top-btn"
        @click="scrollToTop"
        title="Kembali ke atas"
      >
        <ArrowUpOutlined />
      </button>
    </transition>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfPreviewVisible"
      :title="pdfPreviewTitle"
      :pdf-path="pdfPreviewPath"
      :pdf-list="pdfPreviewList"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { notification } from 'ant-design-vue'
import {
  LeftOutlined,
  PaperClipOutlined,
  PlusOutlined,
  MinusOutlined,
  RightOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons-vue'
import {
  suratMasukAPI,
  suratKeluarAPI,
  uploadFilesAPI,
  userAPI,
  disposisiAPI,
  documentExtractionAPI,
} from '@/services/axios/perki'
import { useStore } from 'vuex'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/id'

import {
  VerifikatorTable,
  AlurVerifikasi,
  PenandatanganSelect,
  PerwakilanDinasTable,
  PDFPreviewModal,
} from '@/components/SuratKeluar'

import { useNomorSurat } from '@/composables'
import { useDateFormatting } from '@/composables'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'

dayjs.extend(relativeTime)
dayjs.locale('id')

export default {
  name: 'UpdateSuratMasuk',
  components: {
    PaperClipOutlined,
    PlusOutlined,
    MinusOutlined,
    RightOutlined,
    DeleteOutlined,
    SearchOutlined,
    UploadOutlined,
    ArrowUpOutlined,
    CheckOutlined,
    EyeOutlined,
    QuestionCircleOutlined,
    VerifikatorTable,
    AlurVerifikasi,
    PenandatanganSelect,
    PerwakilanDinasTable,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const formRef = ref(null)
    const fileInput = ref(null)
    const loading = ref(true)
    const submitting = ref(false)
    const suratData = ref(null)
    const validationErrors = ref({})
    const newFile = ref(null)
    const isDetecting = ref(false)

    const userGroupId = localStorage.getItem('perki_group_id')

    // Comment state
    const expandedDisposisiId = ref(null)
    const expandedDisposisiType = ref(null)
    const allCommentsData = ref(null) // Stores all comments grouped by disposisi
    const disposisiComments = ref([])
    const commentsLoading = ref(false)
    const commentsStatusLoading = ref(true) // Loading state for comments status
    const newCommentText = ref('')
    const submittingComment = ref(false)
    const commentActiveTab = ref('surat-keluar')
    const commentFileList = ref([])
    const pollingInterval = ref(null)

    // Scroll to top state
    const showScrollTop = ref(false)
    const scrollThreshold = 1300

    // Confirmation modal state
    const confirmCompleteModalVisible = ref(false)
    const pendingDisposisiId = ref(null)
    const pendingDisposisiType = ref(null)
    const markingComplete = ref(false)

    // Submit confirmation modal state
    const submitConfirmModalVisible = ref(false)

    // Surat Keluar Selection Modal state
    const suratKeluarModalVisible = ref(false)
    const suratKeluarList = ref([])
    const suratKeluarLoading = ref(false)
    const suratKeluarSearch = ref('')
    const selectedSuratKeluar = ref([])

    // PDF Preview Modal state
    const pdfPreviewVisible = ref(false)
    const pdfPreviewTitle = ref('')
    const pdfPreviewPath = ref('')
    const pdfPreviewList = ref([])

    // New disposisi arrays (for adding new ones)
    const newTasks = ref([])

    // Composables
    const { formatForAPI, formatDateTimeForAPI } = useDateFormatting()

    // Unsaved changes warning
    const originalFormData = ref(null)
    const hasFormChanges = computed(() => {
      if (!originalFormData.value) return false
      return JSON.stringify(formData) !== JSON.stringify(originalFormData.value)
    })
    const { setHasUnsavedChanges, resetUnsavedChanges } = useUnsavedChangesWarning({ hasFormChanges })

    const {
      nomorSurat: nomorSuratRef,
      canDecrement,
      fetchLatestNomorSurat,
      incrementNomorSurat: incrementNomorSuratComposable,
      decrementNomorSurat: decrementNomorSuratComposable,
      generateNomorSurat,
      extractNumberFromNomorSurat,
      resetNomorSurat,
    } = useNomorSurat()

    // Nomor surat state for penugasan dinas
    const nomorSuratState = ref({ baseNumber: 0, initialNumber: 0, canDecrement: false })

    // Get pengurus options from store
    const pengurusOptionsFromStore = computed(() => store.state.suratKeluar.pengurusOptions)
    const pengurusMap = computed(() => store.state.suratKeluar.pengurusMap)
    const newPengurusDispositions = ref([])

    // Current user from localStorage
    const currentUser = computed(() => {
      const perkiUser = localStorage.getItem('perki_user')
      return perkiUser ? JSON.parse(perkiUser) : {}
    })

    // Get user role for field access control
    const userRole = computed(() => currentUser.value.role || '')

    const isFieldsDisabled = computed(() => {
      return ['ketua', 'admin', 'pengurus'].includes(userRole.value)
    })

    const formData = reactive({
      pengarsipSurat: '',
      waktuDiregistrasikan: '',
      waktuPenerimaan: dayjs(),
      nomorSurat: '',
      tanggalSurat: null,
      pengirim: '',
      perihal: '',
      keterangan: '',
      disposisi: '',
      pathToFile: '',
      status: '',
      tasks: [],
      pengurusDispositions: [],
      previousDisposisiType: '',
      penugasanDinas: {
        nomorSurat: '',
        sifatSurat: 'biasa',
        bentukSurat: 'Soft copy',
        namaKegiatan: '',
        waktuPerjalanan: null,
        tempatKegiatan: '',
        tanggalSurat: null,
        penandatanganKiriId: null,
        penandatanganKananId: null,
        keperluan: '',
        perwakilan: [
          {
            userId: null,
            searchValue: '',
            isNonPengurus: false,
            namaNonPengurus: null,
            jabatanNonPengurus: null,
            npaNonPengurus: null,
          },
        ],
        verifikator: [
          {
            userId: null,
            urutan: 1,
          },
        ],
      },
    })

    // Existing disposisi (read-only)
    const existingTasks = computed(() => {
      return suratData.value?.disposisiPekerjaHarian || []
    })

    const existingPengurusDispositions = computed(() => {
      return suratData.value?.disposisiPengurus || []
    })

    // Pengurus options for select
    const pengurusOptions = ref([])
    const pekerjaHarianOptions = ref([])

    const FILES_URL = process.env.VUE_APP_PERKI_URL || 'http://localhost:3000'

    // Filter option function for searchable selects
    const filterOption = (input, option) => {
      return option.label.toLowerCase().includes(input.toLowerCase())
    }

    // Disable past dates for deadline task
    const disabledPastDates = current => {
      // Disable dates before today
      return current && current < dayjs().startOf('day')
    }

    // Fetch users in the group
    const fetchGroupUsers = async () => {
      try {
        const response = await await userAPI.getUsersInMyGroup({ limit: 1000 })
        if (response.data?.data?.users) {
          const users = response.data.data.users

          // Filter and format pengurus (excluding pekerja_harian)
          const pengurusList = users
            .filter(user => user.role !== 'pekerja_harian' && user.role !== 'admin')
            .map(user => ({
              label: `${user.name} (${user.jabatan})`,
              value: user.id,
            }))

          // Filter and format pekerja harian
          const pekerjaList = users
            .filter(user => user.role === 'pekerja_harian')
            .map(user => ({
              label: user.name,
              value: user.id,
            }))

          pengurusOptions.value = pengurusList
          pekerjaHarianOptions.value = pekerjaList
        }
      } catch (error) {
        console.error('Error fetching group users:', error)
        notification.error({
          message: 'Error',
          description: 'Error',
          description: 'Gagal memuat daftar pengguna',
        })
      }
    }

    const fetchSuratMasuk = async (silent = false) => {
      const suratId = route.params.id
      if (!suratId) {
        notification.error({
          message: 'Error',
          description: 'Error',
          description: 'ID surat tidak valid',
        })
        router.push('/surat-masuk')
        return
      }

      if (!silent) {
        loading.value = true
      }
      try {
        const response = await suratMasukAPI.getById(suratId)

        if (response.data?.data) {
          suratData.value = response.data.data

          // Pre-fill form data
          formData.pengarsipSurat =
            `${suratData.value.creator?.name} (${suratData.value.creator?.jabatan})` || '-'
          formData.waktuDiregistrasikan = suratData.value.createdAt
            ? dayjs(suratData.value.createdAt).format('dddd YYYY-MM-DD HH:mm')
            : '-'
          formData.waktuPenerimaan = suratData.value.waktuPenerimaan
            ? dayjs(suratData.value.waktuPenerimaan)
            : null
          formData.nomorSurat = suratData.value.nomorSurat || ''
          formData.tanggalSurat = suratData.value.tanggalSurat
            ? dayjs(suratData.value.tanggalSurat)
            : null
          formData.pengirim = suratData.value.pengirim || ''
          formData.perihal = suratData.value.perihal || ''
          formData.keterangan = suratData.value.keterangan || ''
          formData.disposisi = suratData.value.disposisi || ''
          formData.previousDisposisiType = suratData.value.disposisi || ''
          formData.pathToFile = suratData.value.pathToFile || ''
          formData.status = suratData.value.status || ''

          // Initialize arrays
          formData.tasks = []
          formData.pengurusDispositions = []

          // Pre-fill disposisi pengurus if exists (now an array)
          if (
            suratData.value.disposisiPengurus &&
            Array.isArray(suratData.value.disposisiPengurus) &&
            suratData.value.disposisiPengurus.length > 0
          ) {
            formData.pengurusDispositions = suratData.value.disposisiPengurus.map(item => ({
              // disposisiKepada is an array of users, extract the first user's ID
              disposisi:
                item.disposisiKepada && item.disposisiKepada.length > 0
                  ? item.disposisiKepada[0].id
                  : null,
              perihal: item.perihalInstruksi || '',
              instruksi: item.instruksiDisposisi || '',
              isExisting: true, // Mark as existing
            }))
          } else if (
            formData.disposisi === 'pengurus' ||
            formData.disposisi === 'penugasan dinas'
          ) {
            // Initialize empty if pengurus/penugasan dinas but no data
            formData.pengurusDispositions = [
              {
                disposisi: null,
                perihal: '',
                instruksi: '',
                isExisting: false,
              },
            ]
          }

          // Pre-fill disposisi pekerja harian if exists (now an array)
          if (
            suratData.value.disposisiPekerjaHarian &&
            Array.isArray(suratData.value.disposisiPekerjaHarian) &&
            suratData.value.disposisiPekerjaHarian.length > 0
          ) {
            formData.tasks = suratData.value.disposisiPekerjaHarian.map(item => ({
              // disposisiKepada is an array of users, extract all user IDs
              disposisi:
                item.disposisiKepadaUser && item.disposisiKepadaUser.length > 0
                  ? item.disposisiKepadaUser.map(user => user.id)
                  : [],
              judul: item.judulTask || '',
              deadline: item.deadlineTask ? dayjs(item.deadlineTask) : null,
              deskripsi: item.deskripsiTask || '',
              isExisting: true, // Mark as existing
            }))
          } else if (formData.disposisi === 'pekerja harian') {
            // Initialize empty if pekerja harian but no data
            formData.tasks = [
              {
                disposisi: [],
                judul: '',
                deadline: null,
                deskripsi: '',
                isExisting: false,
              },
            ]
          }
        }
      } catch (error) {
        console.error('Error fetching surat masuk:', error)
        notification.error({
          message: 'Error',
          description: 'Error',
          description: 'Gagal memuat data surat masuk',
        })
        suratData.value = null
      } finally {
        if (!silent) {
          loading.value = false
        }
      }
    }

    const triggerFileInput = () => {
      fileInput.value.click()
    }

    // Scroll handling functions
    const handleScroll = () => {
      showScrollTop.value = window.scrollY > scrollThreshold
    }

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    const handleFileChange = event => {
      const file = event.target.files[0]
      if (!file) return

      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (!validTypes.includes(file.type)) {
        notification.error({
          message: 'Error',
          description: 'Format file tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG',
        })
        return
      }

      if (file.size > maxSize) {
        notification.error({
          message: 'Error',
          description: 'Error',
          description: 'Ukuran file terlalu besar. Maksimal 10MB',
        })
        return
      }

      newFile.value = file
      formData.pathToFile = file.name // Temporarily show filename
      notification.success({ message: 'Success', description: `File ${file.name} siap diunggah` })
      event.target.value = ''
    }

    const handleFileDrop = event => {
      event.currentTarget.classList.remove('drag-over')
      const file = event.dataTransfer.files[0]
      if (!file) return

      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (!validTypes.includes(file.type)) {
        notification.error({
          message: 'Error',
          description: 'Format file tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG',
        })
        return
      }

      if (file.size > maxSize) {
        notification.error({
          message: 'Error',
          description: 'Error',
          description: 'Ukuran file terlalu besar. Maksimal 10MB',
        })
        return
      }

      newFile.value = file
      formData.pathToFile = file.name
    }

    const handleDragOver = event => {
      event.currentTarget.classList.add('drag-over')
    }

    const handleDragLeave = event => {
      event.currentTarget.classList.remove('drag-over')
    }

    const deleteFile = () => {
      formData.pathToFile = ''
      newFile.value = null
    }

    const autoDetectWithAI = async () => {
      if (!newFile.value) {
        notification.warning({
          message: 'Warning',
          description: 'Mohon upload file terlebih dahulu',
        })
        return
      }

      isDetecting.value = true

      try {
        // Create FormData for the file
        const uploadFormData = new FormData()
        uploadFormData.append('file', newFile.value)

        // Call backend API to extract document info (backend calls OpenAI)
        const response = await documentExtractionAPI.extractFromFile(uploadFormData)

        if (response.data && response.data.data) {
          const extractedData = response.data.data
          const missingFields = []

          // Map extracted data to form fields and track missing ones
          if (extractedData.no_surat && extractedData.no_surat.trim() !== '') {
            formData.nomorSurat = extractedData.no_surat
          } else {
            missingFields.push('Nomor Surat')
          }

          if (extractedData.tanggal_surat && extractedData.tanggal_surat.trim() !== '') {
            formData.tanggalSurat = dayjs(extractedData.tanggal_surat)
          } else {
            missingFields.push('Tanggal Surat')
          }

          if (extractedData.pengirim && extractedData.pengirim.trim() !== '') {
            formData.pengirim = extractedData.pengirim
          } else {
            missingFields.push('Pengirim')
          }

          if (extractedData.perihal && extractedData.perihal.trim() !== '') {
            formData.perihal = extractedData.perihal
          } else {
            missingFields.push('Perihal')
          }

          // Show result message with details about missing fields
          if (missingFields.length === 0) {
            notification.success({
              message: 'Success',
              description: 'Deteksi otomatis berhasil! Semua data telah terisi lengkap.',
            })
          } else {
            notification.warning({
              message: 'Warning',
              description: `Deteksi berhasil, namun beberapa field tidak terdeteksi: ${missingFields.join(
                ', ',
              )}. Mohon cek kembali.`,
            })
          }
        } else {
          notification.error({ message: 'Error', description: 'Gagal melakukan deteksi otomatis' })
        }
      } catch (error) {
        console.error('Error auto-detecting:', error)
        notification.error({ message: 'Error', description: 'Gagal melakukan deteksi otomatis' })
      } finally {
        isDetecting.value = false
      }
    }

    const addTask = () => {
      formData.tasks.push({
        disposisi: [],
        judul: '',
        deadline: null,
        deskripsi: '',
        isExisting: false,
      })
    }

    const removeTask = taskIndex => {
      formData.tasks.splice(taskIndex, 1)
    }

    const addPengurusDisposition = () => {
      formData.pengurusDispositions.push({
        disposisi: null,
        perihal: '',
        instruksi: '',
        isExisting: false,
      })
    }

    const removePengurusDisposition = dispositionIndex => {
      formData.pengurusDispositions.splice(dispositionIndex, 1)
    }

    const validateFields = () => {
      validationErrors.value = {}
      let hasErrors = false

      // Check if document exists (either existing file or new file uploaded)
      if (!formData.pathToFile && !newFile.value) {
        validationErrors.value.dokumen = 'Dokumen surat wajib diunggah'
        hasErrors = true
      }

      if (!formData.waktuPenerimaan) {
        validationErrors.value.waktuPenerimaan = 'Waktu penerimaan wajib diisi'
        hasErrors = true
      }
      if (!formData.nomorSurat || formData.nomorSurat.trim() === '') {
        validationErrors.value.nomorSurat = 'Nomor surat wajib diisi'
        hasErrors = true
      }
      if (!formData.tanggalSurat) {
        validationErrors.value.tanggalSurat = 'Tanggal surat wajib diisi'
        hasErrors = true
      }
      if (!formData.pengirim || formData.pengirim.trim() === '') {
        validationErrors.value.pengirim = 'Pengirim wajib diisi'
        hasErrors = true
      }
      if (!formData.perihal || formData.perihal.trim() === '') {
        validationErrors.value.perihal = 'Perihal wajib diisi'
        hasErrors = true
      }
      if (!formData.disposisi || formData.disposisi.trim() === '') {
        validationErrors.value.disposisi = 'Disposisi kepada wajib dipilih'
        hasErrors = true
      }

      return !hasErrors
    }

    const handleSubmit = async () => {
      if (!validateFields()) {
        notification.error({
          message: 'Error',
          description: 'Mohon lengkapi semua field yang wajib diisi',
        })
        return
      }

      // Validate new disposisi tasks for pekerja harian
      if (formData.disposisi === 'pekerja harian' && newTasks.value.length > 0) {
        let hasTaskErrors = false
        for (let i = 0; i < newTasks.value.length; i++) {
          const task = newTasks.value[i]
          if (!task.disposisi || task.disposisi.length === 0) {
            validationErrors.value[`task_${i}_disposisi`] = 'Disposisi kepada wajib dipilih'
            hasTaskErrors = true
          }
          if (!task.judul || task.judul.trim() === '') {
            validationErrors.value[`task_${i}_judul`] = 'Judul task wajib diisi'
            hasTaskErrors = true
          }
        }
        if (hasTaskErrors) {
          notification.error({
            message: 'Mohon lengkapi semua field task disposisi yang wajib diisi',
          })
          return
        }
      }

      // Validate new disposisi for pengurus
      if (
        (formData.disposisi === 'pengurus' || formData.disposisi === 'penugasan dinas') &&
        newPengurusDispositions.value.length > 0
      ) {
        let hasPengurusErrors = false
        for (let i = 0; i < newPengurusDispositions.value.length; i++) {
          const pengurus = newPengurusDispositions.value[i]
          if (!pengurus.disposisi) {
            validationErrors.value[`pengurus_${i}_disposisi`] = 'Disposisi kepada wajib dipilih'
            hasPengurusErrors = true
          }
          if (!pengurus.perihal || pengurus.perihal.trim() === '') {
            validationErrors.value[`pengurus_${i}_perihal`] = 'Perihal instruksi wajib diisi'
            hasPengurusErrors = true
          }
        }
        if (hasPengurusErrors) {
          notification.error({
            message: 'Mohon lengkapi semua field disposisi pengurus yang wajib diisi',
          })
          return
        }
      }

      // Validate penugasan dinas when changing from 'tidak didisposisi'
      if (
        formData.disposisi === 'penugasan dinas' &&
        formData.previousDisposisiType === 'tidak didisposisi'
      ) {
        const pd = formData.penugasanDinas
        let hasPDErrors = false

        if (!pd.nomorSurat || pd.nomorSurat.trim() === '') {
          validationErrors.value.pd_nomorSurat = 'Nomor surat tugas wajib diisi'
          hasPDErrors = true
        }
        if (!pd.sifatSurat || pd.sifatSurat.trim() === '') {
          validationErrors.value.pd_sifatSurat = 'Sifat surat wajib dipilih'
          hasPDErrors = true
        }
        if (!pd.bentukSurat || pd.bentukSurat.trim() === '') {
          validationErrors.value.pd_bentukSurat = 'Bentuk surat wajib dipilih'
          hasPDErrors = true
        }
        if (!pd.namaKegiatan || pd.namaKegiatan.trim() === '') {
          validationErrors.value.pd_namaKegiatan = 'Nama kegiatan wajib diisi'
          hasPDErrors = true
        }
        if (!pd.waktuPerjalanan) {
          validationErrors.value.pd_waktuPerjalanan = 'Waktu perjalanan wajib diisi'
          hasPDErrors = true
        }
        if (!pd.tempatKegiatan || pd.tempatKegiatan.trim() === '') {
          validationErrors.value.pd_tempatKegiatan = 'Tempat kegiatan wajib diisi'
          hasPDErrors = true
        }
        if (!pd.tanggalSurat) {
          validationErrors.value.pd_tanggalSurat = 'Tanggal surat wajib dipilih'
          hasPDErrors = true
        }
        if (!pd.penandatanganKiriId) {
          validationErrors.value.pd_penandatanganKiri = 'Penandatangan kiri wajib diisi'
          hasPDErrors = true
        }
        if (!pd.keperluan || pd.keperluan.trim() === '') {
          validationErrors.value.pd_keperluan = 'Keperluan wajib diisi'
          hasPDErrors = true
        }
        if (
          !pd.perwakilan ||
          pd.perwakilan.length === 0 ||
          !pd.perwakilan.some(p => p.userId || p.isNonPengurus)
        ) {
          validationErrors.value.pd_perwakilan = 'Minimal harus ada satu perwakilan dinas'
          hasPDErrors = true
        }
        if (
          pd.perwakilan.some(
            p =>
              p.isNonPengurus &&
              (!p.namaNonPengurus ||
                !p.jabatanNonPengurus ||
                !p.npaNonPengurus ||
                p.namaNonPengurus.trim() === '' ||
                p.jabatanNonPengurus.trim() === '' ||
                p.npaNonPengurus.trim() === ''),
          )
        ) {
          validationErrors.value.pd_perwakilan =
            'Mohon isi Nama, Jabatan, dan NPA untuk setiap perwakilan dinas'
          hasPDErrors = true
        }

        if (hasPDErrors) {
          notification.error({
            message: 'Error',
            description: 'Mohon lengkapi semua field yang wajib diisi',
          })
          return
        }
      }

      // Show confirmation modal
      submitConfirmModalVisible.value = true
    }

    const confirmSubmit = async () => {
      submitConfirmModalVisible.value = false
      submitting.value = true

      try {
        let finalPathToFile = formData.pathToFile
        let isFileReplaced = false

        // Step 1: Upload new file if it was replaced
        if (newFile.value) {
          const uploadFormData = new FormData()
          // Append all files (currently only one, but supports multiple)
          if (Array.isArray(newFile.value)) {
            newFile.value.forEach(file => {
              uploadFormData.append('files', file)
            })
          } else {
            uploadFormData.append('files', newFile.value)
          }

          const uploadResponse = await uploadFilesAPI.uploadFiles(uploadFormData)

          if (!uploadResponse.data?.data?.uploaded?.[0]) {
            throw new Error('Gagal mengunggah dokumen')
          }

          finalPathToFile = uploadResponse.data.data.uploaded[0].path
          isFileReplaced = true
        }

        // Step 2: Create surat keluar if penugasan dinas from 'tidak didisposisi'
        let suratKeluarId = null
        if (
          formData.disposisi === 'penugasan dinas' &&
          formData.previousDisposisiType === 'tidak didisposisi'
        ) {
          const pd = formData.penugasanDinas

          // Prepare surat keluar data
          const suratKeluarData = {
            jenisSurat: 'Surat Tugas',
            nomorSurat: pd.nomorSurat,
            sifatSurat: pd.sifatSurat,
            bentukSurat: pd.bentukSurat,
            namaKegiatan: pd.namaKegiatan,
            suratUndanganAcaraId: suratData.value.id || null,
            waktuPerjalanan: formatDateTimeForAPI(pd.waktuPerjalanan),
            tempatKegiatan: pd.tempatKegiatan,
            tanggalSurat: formatForAPI(pd.tanggalSurat),
            penandatanganKiriId: pd.penandatanganKiriId,
            penandatanganKananId: pd.penandatanganKananId || null,
            keperluan: pd.keperluan,
            perwakilanDinas: pd.perwakilan.map(p => {
              if (p.isNonPengurus) {
                return {
                  isNonPengurus: true,
                  namaNonPengurus: p.namaNonPengurus,
                  jabatanNonPengurus: p.jabatanNonPengurus,
                  npaNonPengurus: p.npaNonPengurus,
                }
              } else {
                return { userId: p.userId }
              }
            }),
            verifikatorIds: pd.verifikator.filter(v => v.userId).map(v => v.userId),
            isDraft: false,
          }

          try {
            const suratKeluarResponse = await suratKeluarAPI.create(suratKeluarData)
            suratKeluarId = suratKeluarResponse.data?.data?.id
          } catch (error) {
            console.error('Error creating surat keluar:', error)
            const errorMsg = error.response?.data?.message || error.message
            notification.error({
              message: 'Gagal membuat surat tugas',
              description: errorMsg,
            })
            throw error
          }
        }

        // Step 3: Build request body
        const requestBody = {
          pathToFile: finalPathToFile,
          isFileReplaced: isFileReplaced,
          waktuPenerimaan: formData.waktuPenerimaan
            ? dayjs(formData.waktuPenerimaan).toISOString()
            : null,
          nomorSurat: formData.nomorSurat,
          tanggalSurat: formData.tanggalSurat ? dayjs(formData.tanggalSurat).toISOString() : null,
          pengirim: formData.pengirim,
          perihal: formData.perihal,
          keterangan: formData.keterangan || '',
          disposisi: formData.disposisi,
          disposisiDetail: null,
          suratKeluarId: suratKeluarId, // Include suratKeluarId if created
        }

        // Add disposisi detail based on type (only new items, not existing)
        if (formData.disposisi === 'pekerja harian' && newTasks.value.length > 0) {
          // Send only new tasks
          requestBody.disposisiDetail = newTasks.value
            .filter(task => task.disposisi?.length > 0 && task.judul)
            .map(task => ({
              disposisiKepada: task.disposisi || [],
              judulTask: task.judul,
              deadlineTask: task.deadline ? dayjs(task.deadline).toISOString() : null,
              deskripsiTask: task.deskripsi || '',
            }))
        } else if (formData.disposisi === 'pengurus' && newPengurusDispositions.value.length > 0) {
          // Send only new pengurus dispositions
          requestBody.disposisiDetail = newPengurusDispositions.value
            .filter(pengurus => pengurus.disposisi && pengurus.perihal)
            .map(pengurus => ({
              disposisiKepada: pengurus.disposisi ? [pengurus.disposisi] : [],
              perihalInstruksi: pengurus.perihal,
              instruksiDisposisi: pengurus.instruksi || '',
            }))
        } else if (formData.disposisi === 'penugasan dinas' && formData.penugasanDinas) {
          // Handle penugasan dinas - include suratKeluarId from the created surat keluar
          requestBody.disposisiDetail = {
            nomorSurat: formData.penugasanDinas.nomorSurat,
            sifatSurat: formData.penugasanDinas.sifatSurat,
            bentukSurat: formData.penugasanDinas.bentukSurat,
            namaKegiatan: formData.penugasanDinas.namaKegiatan,
            waktuPerjalanan: formatDateTimeForAPI(formData.penugasanDinas.waktuPerjalanan),
            tempatKegiatan: formData.penugasanDinas.tempatKegiatan,
            penandatangan1: formData.penugasanDinas.penandatangan1,
            penandatangan2: formData.penugasanDinas.penandatangan2,
            keperluan: formData.penugasanDinas.keperluan,
            perwakilan: formData.penugasanDinas.perwakilan,
            verifikator: formData.penugasanDinas.verifikator,
            suratKeluarId: suratKeluarId || null,
          }
        }

        // Step 3: Submit update
        await suratMasukAPI.update(route.params.id, requestBody)

        notification.success({
          message: 'Success',
          description: 'Data surat masuk berhasil diubah.',
        })
        resetUnsavedChanges()
        router.push('/surat-masuk')
      } catch (error) {
        console.error('Error updating surat masuk:', error)
        if (error.response?.data?.message) {
          notification.error({ message: 'Error', description: error.response.data.message })
        } else {
          notification.error({ message: 'Error', description: 'Gagal menyimpan perubahan' })
        }
      } finally {
        submitting.value = false
      }
    }

    const getFileName = path => {
      if (!path) return 'File tidak tersedia'

      const parts = path.split('/')
      return parts[parts.length - 1]
    }

    // Get surat tugas file name from pdfPath (e.g., "surat/03_Surat Tugas Pengurus Pusat 1_fghjk.pdf")
    const getSuratTugasFileName = pdfPath => {
      if (!pdfPath) return 'Surat Tugas'
      const parts = pdfPath.split('/')
      const fileName = parts[parts.length - 1]
      // Remove .pdf extension and return
      return fileName.replace('.pdf', '')
    }

    const getFileUrl = path => {
      if (!path) return '#'
      return `${FILES_URL}${path}`
    }

    const openFilePreview = () => {
      // If there's a new file uploaded (not yet saved to backend)
      if (newFile.value) {
        const blobUrl = URL.createObjectURL(newFile.value)
        window.open(blobUrl, '_blank')
        // Clean up the blob URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl)
        }, 100)
      }
      // If it's an existing file from backend
      else if (formData.pathToFile) {
        window.open(getFileUrl(formData.pathToFile), '_blank')
      }
    }

    // Check if attachment is a surat keluar object
    const isSuratKeluarAttachment = attachment => {
      return typeof attachment === 'object' && attachment !== null && attachment.suratKeluarId
    }

    // Get attachment display name
    const getAttachmentName = attachment => {
      if (isSuratKeluarAttachment(attachment)) {
        return attachment.name || 'Surat Keluar'
      }
      return getFileName(attachment) || 'Draft Surat Rekomendasi'
    }

    // Navigate to surat keluar detail page - opens PDF preview modal
    const navigateToSuratKeluar = async attachment => {
      try {
        // Set the title from attachment name
        pdfPreviewTitle.value = 'Preview Surat Keluar'

        // Handle pdfPaths array (new structure)
        if (attachment.pdfPaths && attachment.pdfPaths.length > 0) {
          if (attachment.pdfPaths.length === 1) {
            // Single PDF - use pdfPath prop
            pdfPreviewPath.value = attachment.pdfPaths[0]
            pdfPreviewList.value = []
          } else {
            // Multiple PDFs - use pdfList prop
            pdfPreviewPath.value = ''
            pdfPreviewList.value = attachment.pdfPaths.map((path, index) => ({
              path: path,
              name: `Dokumen ${index + 1}`,
            }))
          }
          pdfPreviewVisible.value = true
          return
        }

        // Handle single pdfPath (old structure)
        if (attachment.pdfPath) {
          pdfPreviewPath.value = attachment.pdfPath
          pdfPreviewList.value = []
          pdfPreviewVisible.value = true
          return
        }

        // If no path available, fetch from API
        if (attachment.suratKeluarId) {
          const response = await suratKeluarAPI.getById(attachment.suratKeluarId)
          const suratKeluar = response.data?.data || response.data
          pdfPreviewTitle.value = 'Preview Surat Keluar'
          pdfPreviewPath.value = suratKeluar.pdfPath || ''
          pdfPreviewList.value = []
          pdfPreviewVisible.value = true
        }
      } catch (error) {
        console.error('Error fetching surat keluar:', error)
        notification.error({
          message: 'Error',
          description: 'Gagal memuat surat keluar',
        })
      }
    }

    const goBack = () => {
      router.push('/surat-masuk')
    }

    // Open surat tugas preview in modal
    const openSuratTugasPreview = perwakilan => {
      if (perwakilan.pdfPath) {
        pdfPreviewTitle.value = 'Preview Surat Tugas'
        pdfPreviewPath.value = perwakilan.pdfPath
        pdfPreviewList.value = []
        pdfPreviewVisible.value = true
      }
    }

    // New helper functions for disposisi
    const getDisposisiKepadaNames = disposisiKepada => {
      if (!disposisiKepada || disposisiKepada.length === 0) return '-'
      return disposisiKepada.map(user => `${user.name} (${user.jabatan})`).join(', ')
    }

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      return dayjs(dateString).format('dddd YYYY-MM-DD HH:mm')
    }

    const formatCommentTime = dateString => {
      if (!dateString) return ''
      return dayjs(dateString).format('DD MMM YYYY, HH:mm') + ` (${dayjs(dateString).fromNow()})`
    }

    const getStatusColor = status => {
      switch (status) {
        case 'Selesai Disposisi':
          return 'green'
        case 'Didisposisikan':
          return 'orange'
        case 'Diarsipkan':
          return 'blue'
        default:
          return 'default'
      }
    }

    // Get substatus for a disposisi based on comments
    const getDisposisiSubstatus = (disposisiId, type) => {
      if (!allCommentsData.value) {
        return { label: 'Baru', color: 'red' }
      }

      let comments = []
      if (type === 'pekerja-harian') {
        const disposisiData = allCommentsData.value.disposisiPekerjaHarian?.find(
          d => d.disposisiId === disposisiId,
        )
        comments = disposisiData?.comments || []
      } else if (type === 'pengurus') {
        const disposisiData = allCommentsData.value.disposisiPengurus?.find(
          d => d.disposisiId === disposisiId,
        )
        comments = disposisiData?.comments || []
      }

      // No comments yet
      if (comments.length === 0) {
        return { label: 'Baru', color: 'red' }
      }

      // Get the latest comment
      const latestComment = comments[comments.length - 1]

      // Check if the latest comment is from the current user
      if (latestComment.userId === currentUser.value?.id) {
        return { label: 'Menunggu', color: 'blue' }
      } else {
        return { label: 'Dijawab', color: 'pink' }
      }
    }

    const isDisposisiCreator = disposisi => {
      return disposisi.createdBy === currentUser.value?.id
    }

    const displayCommentsStatus = () => {
      if (!suratData.value || !allCommentsData.value) return false

      // Get all disposisi pengurus created by the current user
      const userDispositions =
        suratData.value.disposisiPengurus?.filter(d => d.createdBy === currentUser.value?.id) || []

      if (userDispositions.length === 0) return false

      // Check if any of the user's dispositions have a latest comment from another user
      const hasLatestCommentFromOther = userDispositions.some(disposisi => {
        const disposisiData = allCommentsData.value.disposisiPengurus?.find(
          d => d.disposisiId === disposisi.id,
        )
        const comments = disposisiData?.comments || []

        if (comments.length === 0) return false

        const latestComment = comments[comments.length - 1]
        return latestComment.userId !== currentUser.value?.id
      })

      return hasLatestCommentFromOther
    }

    // Get overall comments status for all dispositions
    const getCommentsStatus = () => {
      if (!allCommentsData.value) return 'Baru'

      // Collect all dispositions (both pekerja-harian and pengurus)
      const allDispositions = [
        ...(allCommentsData.value.disposisiPekerjaHarian || []),
        ...(allCommentsData.value.disposisiPengurus || []),
      ]

      if (allDispositions.length === 0) return 'Baru'

      const activeDispositions = allDispositions.filter(
        disposisi => disposisi.statusDisposisi !== 'Selesai Disposisi',
      )

      // Check if any active disposisi has latest comment from other user
      const hasLatestCommentFromOther = activeDispositions.some(disposisi => {
        const comments = disposisi.comments || []

        // No comments yet
        if (comments.length === 0) {
          return false
        }

        if (disposisi.statusDisposisi !== 'Didisposisikan') {
          return false
        }

        // Get the latest comment
        const latestComment = comments[comments.length - 1]
        return latestComment.userId !== currentUser.value?.id
      })

      if (hasLatestCommentFromOther) {
        return 'Dijawab'
      }

      // Check if any active disposisi has any comments at all
      const hasAnyComments = activeDispositions.some(disposisi => {
        const comments = disposisi.comments || []
        return comments.length > 0
      })

      if (hasAnyComments) {
        return 'Menunggu'
      }

      return 'Baru'
    }

    // Get color for comments status
    const getCommentsStatusColor = () => {
      const status = getCommentsStatus()
      switch (status) {
        case 'Dijawab':
          return 'pink'
        case 'Menunggu':
          return 'blue'
        case 'Baru':
          return 'red'
        default:
          return 'default'
      }
    }

    // Check if a disposisi is completed
    const isDisposisiCompleted = (disposisiId, type) => {
      if (!suratData.value) return false

      if (type === 'pekerja-harian') {
        const disposisi = suratData.value.disposisiPekerjaHarian?.find(d => d.id === disposisiId)
        return disposisi?.statusDisposisi === 'Selesai Disposisi'
      } else if (type === 'pengurus') {
        const disposisi = suratData.value.disposisiPengurus?.find(d => d.id === disposisiId)
        return disposisi?.statusDisposisi === 'Selesai Disposisi'
      }
      return false
    }

    // Check if all dispositions are completed
    const areAllDispositionsCompleted = () => {
      if (!suratData.value) return false

      const allDispositions = [
        ...(suratData.value.disposisiPekerjaHarian || []),
        ...(suratData.value.disposisiPengurus || []),
      ]

      if (allDispositions.length === 0) return false

      return allDispositions.every(d => d.statusDisposisi === 'Selesai Disposisi')
    }

    const isCommentOwner = comment => {
      return comment.userId === currentUser.value?.id
    }

    // Check if current user can comment on this disposisi pengurus
    // Only the creator of the surat masuk can comment
    const canCommentOnDisposisi = (disposisiId, type) => {
      if (type !== 'pengurus') return true // Allow comments on other types

      // Check if current user is the creator of the surat masuk
      return suratData.value?.creator?.id === currentUser.value?.id
    }

    const beforeCommentUpload = file => {
      // Return false to prevent automatic upload - we'll handle it in handleCommentFileChange
      return false
    }

    const handleCommentFileChange = ({ fileList }) => {
      // Update commentFileList with new files, preserving existing surat keluar attachments
      const newFiles = fileList.filter(
        file => !commentFileList.value.some(existingFile => existingFile.uid === file.uid),
      )
      commentFileList.value = [...commentFileList.value, ...newFiles]
    }

    const removeCommentFile = index => {
      commentFileList.value.splice(index, 1)
    }

    const toggleDisposisiComments = async (disposisiId, type) => {
      if (expandedDisposisiId.value === disposisiId) {
        // Collapse
        expandedDisposisiId.value = null
        expandedDisposisiType.value = null
        disposisiComments.value = []
      } else {
        // Expand and fetch comments
        expandedDisposisiId.value = disposisiId
        expandedDisposisiType.value = type
        await fetchAllComments()
        // Find comments for this specific disposisi
        findCommentsForDisposisi(disposisiId, type)
      }
    }

    const fetchAllComments = async (force = false) => {
      // Only fetch if we don't have data yet, unless force is true
      if (allCommentsData.value && !force) {
        return
      }

      commentsLoading.value = true
      try {
        const suratId = route.params.id
        const response = await suratMasukAPI.getComments(suratId)
        allCommentsData.value = response.data?.data || null
      } catch (error) {
        console.error('Error fetching comments:', error)
        allCommentsData.value = null
      } finally {
        commentsLoading.value = false
        commentsStatusLoading.value = false // Comments status is ready
      }
    }

    const startPolling = () => {
      // Only poll for pengurus type, not for pekerja harian
      if (formData.disposisi !== 'pengurus') {
        return
      }

      // Clear any existing interval
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
      }

      // Poll every 10 seconds
      pollingInterval.value = setInterval(async () => {
        try {
          // Check if all dispositions are completed before polling
          if (areAllDispositionsCompleted()) {
            // Refresh surat data to get updated status from server
            await fetchSuratMasuk(true)
            stopPolling()
            return
          }

          const suratId = route.params.id
          const response = await suratMasukAPI.getComments(suratId)
          allCommentsData.value = response.data?.data || null

          // Refresh surat data to check for status updates (silent mode - no loading state)
          // await fetchSuratMasuk(true)

          // Update the current disposisi comments if one is expanded
          if (expandedDisposisiId.value && expandedDisposisiType.value) {
            findCommentsForDisposisi(expandedDisposisiId.value, expandedDisposisiType.value, false)
          }
        } catch (error) {
          console.error('Error polling comments:', error)
        }
      }, 10000)
    }

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
      }
    }

    const findCommentsForDisposisi = (disposisiId, type, shouldScroll = true) => {
      if (!allCommentsData.value) {
        disposisiComments.value = []
        return
      }

      const previousLength = disposisiComments.value.length

      if (type === 'pekerja-harian') {
        const disposisiData = allCommentsData.value.disposisiPekerjaHarian?.find(
          d => d.disposisiId === disposisiId,
        )
        disposisiComments.value = disposisiData?.comments || []
      } else if (type === 'pengurus') {
        const disposisiData = allCommentsData.value.disposisiPengurus?.find(
          d => d.disposisiId === disposisiId,
        )
        disposisiComments.value = disposisiData?.comments || []
      } else {
        disposisiComments.value = []
      }

      // Only scroll if there are new comments or if explicitly requested
      if (shouldScroll || disposisiComments.value.length > previousLength) {
        scrollCommentsToBottom()
      }
    }
    const scrollCommentsToBottom = () => {
      nextTick(() => {
        const commentsList = document.querySelector('.disposisi-comments-section .comments-list')
        if (commentsList) {
          commentsList.scrollTop = commentsList.scrollHeight
        }
      })
    }

    const refreshComments = async () => {
      // Force refetch comments
      allCommentsData.value = null
      await fetchAllComments()
      if (expandedDisposisiId.value && expandedDisposisiType.value) {
        findCommentsForDisposisi(expandedDisposisiId.value, expandedDisposisiType.value)
      }
    }

    const submitDisposisiComment = async () => {
      if (!newCommentText.value.trim() && !commentFileList.value.length) {
        notification.warning({
          message: 'Warning',
          description: 'Silakan isi komentar atau unggah file',
        })
        return
      }

      submittingComment.value = true
      try {
        let attachments = []

        // Separate surat keluar from user-uploaded files
        const suratKeluarFiles = commentFileList.value.filter(f => f.type === 'surat-keluar')
        const uploadedFiles = commentFileList.value.filter(f => f.type !== 'surat-keluar')

        // Upload user files if exist
        if (uploadedFiles.length > 0) {
          const formData = new FormData()
          // Append all files from the list
          uploadedFiles.forEach(fileItem => {
            const file = fileItem.originFileObj || fileItem
            formData.append('files', file)
          })

          const uploadResponse = await uploadFilesAPI.uploadFiles(formData)
          if (uploadResponse.data?.data?.uploaded) {
            // Add uploaded file paths as strings
            attachments = uploadResponse.data.data.uploaded.map(file => file.path)
          }
        }

        // Add surat keluar files as objects with name and suratKeluarId
        suratKeluarFiles.forEach(suratKeluar => {
          attachments.push({
            name: suratKeluar.name,
            suratKeluarId: suratKeluar.suratKeluarId,
          })
        })

        // Create comment with mixed attachments (strings and objects)
        await disposisiAPI.createComment(expandedDisposisiType.value, expandedDisposisiId.value, {
          content: newCommentText.value.trim() || '',
          attachments: attachments,
        })

        // Reset form
        newCommentText.value = ''
        commentFileList.value = []

        // Refresh comments
        await refreshComments()
      } catch (error) {
        console.error('Error submitting comment:', error)
        notification.error({ message: 'Error', description: 'Gagal mengirim komentar' })
      } finally {
        submittingComment.value = false
      }
    }

    const markDisposisiComplete = (disposisiId, type) => {
      // Show confirmation modal
      pendingDisposisiId.value = disposisiId
      pendingDisposisiType.value = type
      confirmCompleteModalVisible.value = true
    }

    const confirmMarkDisposisiComplete = async () => {
      try {
        markingComplete.value = true
        // Call API to mark disposisi as complete
        const response = await disposisiAPI.updateStatus(
          pendingDisposisiType.value,
          pendingDisposisiId.value,
        )
        notification.success({
          message: 'Success',
          description: 'Disposisi ditandai sebagai selesai',
        })
        confirmCompleteModalVisible.value = false

        // Refresh surat data to get updated statuses
        await fetchSuratMasuk()

        // Check if all dispositions are now completed
        if (areAllDispositionsCompleted()) {
          // Stop polling since all dispositions are completed
          stopPolling()
        }
      } catch (error) {
        console.error('Error marking disposisi complete:', error)
        notification.error({
          message: 'Error',
          description: 'Gagal menandai disposisi sebagai selesai',
        })
      } finally {
        markingComplete.value = false
        pendingDisposisiId.value = null
        pendingDisposisiType.value = null
      }
    }

    const cancelMarkComplete = () => {
      confirmCompleteModalVisible.value = false
      pendingDisposisiId.value = null
      pendingDisposisiType.value = null
    }

    // New task functions
    const addNewTask = () => {
      newTasks.value.push({
        disposisi: [],
        judul: '',
        deadline: null,
        deskripsi: '',
      })
    }

    const removeNewTask = taskIndex => {
      newTasks.value.splice(taskIndex, 1)
    }

    const addNewPengurusDisposition = () => {
      newPengurusDispositions.value.push({
        disposisi: null,
        perihal: '',
        instruksi: '',
      })
    }

    const removeNewPengurusDisposition = dispositionIndex => {
      newPengurusDispositions.value.splice(dispositionIndex, 1)
    }

    // Penugasan Dinas helper functions
    const addPerwakilanDinas = () => {
      formData.penugasanDinas.perwakilan.push({
        userId: null,
        searchValue: '',
        isNonPengurus: false,
        namaNonPengurus: null,
        jabatanNonPengurus: null,
        npaNonPengurus: null,
      })
    }

    const removePerwakilanDinas = rowIndex => {
      formData.penugasanDinas.perwakilan.splice(rowIndex, 1)
    }

    // Handle preview for Perwakilan Dinas row
    const handlePreviewPerwakilan = async ({ index, record }) => {
      // Check if record has either userId (pengurus) or non-pengurus data
      if (!record.userId && !record.isNonPengurus) {
        notification.warning({ message: 'Silakan pilih perwakilan dinas terlebih dahulu' })
        return
      }

      // For non-pengurus, require all fields
      if (record.isNonPengurus) {
        if (!record.namaNonPengurus || !record.jabatanNonPengurus || !record.npaNonPengurus) {
          notification.warning({
            message: 'Data tidak lengkap',
            description: 'Lengkapi Nama, Jabatan, dan NPA untuk non-pengurus',
          })
          return
        }
      }

      // Minimal validation - only require perwakilan to be selected
      if (!formData.penugasanDinas.namaKegiatan) {
        notification.warning({
          message: 'Nama Kegiatan diperlukan',
          description: 'Minimal isi Nama Kegiatan untuk melihat preview surat',
        })
        return
      }

      try {
        // Prepare preview data - send whatever is currently filled
        const previewData = {
          jenisSurat: 'Surat Tugas',
          nomorSurat: formData.penugasanDinas.nomorSurat || '[NOMOR BELUM DIISI]',
          sifatSurat: formData.penugasanDinas.sifatSurat || 'Biasa',
          bentukSurat: formData.penugasanDinas.bentukSurat || 'Soft Copy',
          tanggalSurat: formData.penugasanDinas.tanggalSurat
            ? formData.penugasanDinas.tanggalSurat.format('DD-MM-YYYY')
            : null,
          namaKegiatan: formData.penugasanDinas.namaKegiatan,
          waktuPerjalanan: formatDateTimeForAPI(formData.penugasanDinas.waktuPerjalanan),
          tempatKegiatan: formData.penugasanDinas.tempatKegiatan || null,
          keperluan: formData.penugasanDinas.keperluan || null,
          penandatanganKiriId: formData.penugasanDinas.penandatanganKiriId || null,
          penandatanganKananId: formData.penugasanDinas.penandatanganKananId || null,
        }

        // Add perwakilan data based on type (pengurus or non-pengurus)
        if (record.isNonPengurus) {
          previewData.isNonPengurus = true
          previewData.namaNonPengurus = record.namaNonPengurus
          previewData.jabatanNonPengurus = record.jabatanNonPengurus
          previewData.npaNonPengurus = record.npaNonPengurus
        } else {
          previewData.perwakilanDinasUserId = record.userId
        }

        // Call preview API
        const response = await suratKeluarAPI.previewPerwakilanDinasPDF(previewData)

        // Create blob URL for modal preview
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)

        // Get perwakilan name for modal title
        let perwakilanName = 'Perwakilan'
        if (record.isNonPengurus) {
          perwakilanName = record.namaNonPengurus || 'Non-Pengurus'
        } else {
          const perwakilan = pengurusMap.value[record.userId]
          perwakilanName = perwakilan?.name || 'Perwakilan'
        }

        pdfPreviewTitle.value = `Preview Surat Tugas - ${perwakilanName}`
        pdfPreviewPath.value = blobUrl
        pdfPreviewList.value = []
        pdfPreviewVisible.value = true

        // Clean up blob URL after modal is closed (5 seconds after opening)
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000)
      } catch (error) {
        console.error('Preview error:', error)
        notification.error({
          message: 'Gagal membuat preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
        })
      }
    }

    const addVerifikator = () => {
      const nextUrutan = formData.penugasanDinas.verifikator.length + 1
      formData.penugasanDinas.verifikator.push({
        userId: null,
        urutan: nextUrutan,
      })
    }

    const removeVerifikator = rowIndex => {
      formData.penugasanDinas.verifikator.splice(rowIndex, 1)
      // Reorder urutan
      formData.penugasanDinas.verifikator.forEach((item, idx) => {
        item.urutan = idx + 1
      })
    }

    // Helper functions for pengurus data using store getters
    const getPengurusJabatan = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.jabatan || ''
    }

    const getPengurusNPA = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.npa || ''
    }

    const getPengurusName = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.name || ''
    }

    const filterPengurusOption = (input, option) => {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Nomor surat increment/decrement functions
    const incrementNomorSurat = () => {
      const currentNomor = formData.penugasanDinas.nomorSurat
      const currentNumber = currentNomor ? extractNumberFromNomorSurat(currentNomor) : 0
      const newNumber = currentNumber + 1

      formData.penugasanDinas.nomorSurat = generateNomorSurat(newNumber)

      // Update state
      nomorSuratState.value.baseNumber = newNumber
      nomorSuratState.value.canDecrement = newNumber > nomorSuratState.value.initialNumber
    }

    const decrementNomorSurat = () => {
      if (!nomorSuratState.value.canDecrement) return

      const currentNomor = formData.penugasanDinas.nomorSurat
      const currentNumber = currentNomor
        ? extractNumberFromNomorSurat(currentNomor)
        : nomorSuratState.value.initialNumber

      // Can only decrement down to the initial number
      if (currentNumber > nomorSuratState.value.initialNumber) {
        const newNumber = currentNumber - 1
        formData.penugasanDinas.nomorSurat = generateNomorSurat(newNumber)

        nomorSuratState.value.baseNumber = newNumber
        // Disable decrement when we reach the initial number
        nomorSuratState.value.canDecrement = newNumber > nomorSuratState.value.initialNumber
      }
    }

    // Surat Keluar Modal functions
    const openSuratKeluarModal = async () => {
      suratKeluarModalVisible.value = true
      suratKeluarLoading.value = true
      suratKeluarSearch.value = ''
      selectedSuratKeluar.value = []
      try {
        const response = await suratKeluarAPI.getAll({ groupId: userGroupId })
        if (response.data?.data?.suratKeluar) {
          suratKeluarList.value = response.data.data.suratKeluar
        }
      } catch (error) {
        console.error('Error fetching surat keluar:', error)
        notification.error({ message: 'Error', description: 'Gagal memuat daftar surat keluar' })
      } finally {
        suratKeluarLoading.value = false
      }
    }

    const filteredSuratKeluar = computed(() => {
      if (!suratKeluarSearch.value) {
        return suratKeluarList.value
      }
      const search = suratKeluarSearch.value.toLowerCase()
      return suratKeluarList.value.filter(
        surat =>
          (surat.nomorSurat || '').toLowerCase().includes(search) ||
          (surat.jenisSurat || '').toLowerCase().includes(search) ||
          (surat.perihal || '').toLowerCase().includes(search),
      )
    })

    const suratKeluarColumns = [
      {
        title: 'Nomor Surat',
        dataIndex: 'nomorSurat',
        key: 'nomorSurat',
        width: 140,
      },
      {
        title: 'Tanggal Surat',
        dataIndex: 'tanggalSurat',
        key: 'tanggalSurat',
        width: 130,
      },
      {
        title: 'Jenis Surat',
        dataIndex: 'jenisSurat',
        key: 'jenisSurat',
        width: 130,
      },
      {
        title: 'Perihal',
        dataIndex: 'namaKegiatan',
        key: 'namaKegiatan',
        width: 180,
      },
      {
        title: 'Pengaju',
        dataIndex: 'creator.name',
        key: 'pengaju',
        width: 120,
      },
    ]

    const onSuratKeluarSelectionChange = selectedRowKeys => {
      selectedSuratKeluar.value = selectedRowKeys
    }

    const handleAddSelectedSuratKeluar = record => {
      // Add selected surat keluar to commentFileList
      if (record.pdfPath) {
        const suratFile = {
          name: `Surat Keluar - ${record.nomorSurat}`,
          suratKeluarId: record.id,
          type: 'surat-keluar',
        }
        commentFileList.value = [...commentFileList.value, suratFile]
        notification.success({
          message: 'Success',
          description: 'Surat keluar berhasil ditambahkan',
        })
      } else {
        notification.warning({
          message: 'Warning',
          description: 'Warning',
          description: 'Surat keluar ini belum memiliki file PDF',
        })
      }
      suratKeluarModalVisible.value = false
    }

    const confirmSuratKeluarSelection = () => {
      // Add all selected surat keluar to commentFileList
      const selectedRecords = suratKeluarList.value.filter(surat =>
        selectedSuratKeluar.value.includes(surat.id),
      )

      selectedRecords.forEach(record => {
        const suratFile = {
          name: `Surat Keluar - ${record.nomorSurat}`,
          suratKeluarId: record.id,
          type: 'surat-keluar',
        }
        // Avoid duplicates - check by suratKeluarId
        if (!commentFileList.value.some(f => f.suratKeluarId === record.id)) {
          commentFileList.value = [...commentFileList.value, suratFile]
        }
      })
      suratKeluarModalVisible.value = false
      selectedSuratKeluar.value = []
    }

    const closeSuratKeluarModal = () => {
      suratKeluarModalVisible.value = false
      selectedSuratKeluar.value = []
      suratKeluarSearch.value = ''
    }

    onMounted(async () => {
      if (userGroupId) {
        await fetchGroupUsers()
      }
      await fetchSuratMasuk()
      // Fetch pengurus options from store for penugasan dinas
      await store.dispatch('suratKeluar/fetchPengurusOptions')
      // Fetch latest nomor surat for penugasan dinas
      await fetchLatestNomorSurat()
      // Initialize nomor surat for penugasan dinas
      formData.penugasanDinas.nomorSurat = nomorSuratRef.value
      // Store the initial number
      nomorSuratState.value.initialNumber = extractNumberFromNomorSurat(nomorSuratRef.value)
      nomorSuratState.value.baseNumber = nomorSuratState.value.initialNumber
      // Fetch comments on page load
      await fetchAllComments(true)
      // Start polling for comments updates
      startPolling()

      // Add scroll event listener
      window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
      stopPolling()
      // Remove scroll event listener
      window.removeEventListener('scroll', handleScroll)
    })

    // Watch for disposisi type changes from 'tidak didisposisi'
    watch(
      () => formData.disposisi,
      (newValue, oldValue) => {
        // Only initialize when changing from 'tidak didisposisi' to another type
        if (formData.previousDisposisiType === 'tidak didisposisi') {
          if (newValue === 'pekerja harian' && newTasks.value.length === 0) {
            // Add initial task card for pekerja harian
            newTasks.value.push({
              disposisi: [],
              judul: '',
              deadline: null,
              deskripsi: '',
            })
          } else if (newValue === 'pengurus' && newPengurusDispositions.value.length === 0) {
            // Add initial pengurus disposition card
            newPengurusDispositions.value.push({
              disposisi: null,
              perihal: '',
              instruksi: '',
            })
          } else if (
            oldValue === 'pekerja harian' &&
            newValue !== 'pekerja harian' &&
            formData.previousDisposisiType === 'tidak didisposisi'
          ) {
            // Clear tasks when switching away from pekerja harian
            newTasks.value = []
          } else if (
            oldValue === 'pengurus' &&
            newValue !== 'pengurus' &&
            formData.previousDisposisiType === 'tidak didisposisi'
          ) {
            // Clear pengurus dispositions when switching away from pengurus
            newPengurusDispositions.value = []
          }
        }
      },
    )

    return {
      formRef,
      fileInput,
      loading,
      submitting,
      suratData,
      formData,
      validationErrors,
      pengurusOptions,
      pekerjaHarianOptions,
      pengurusOptionsFromStore,
      filterOption,
      disabledPastDates,
      pengurusMap,
      nomorSuratState,
      // New disposisi state
      existingTasks,
      existingPengurusDispositions,
      newTasks,
      newPengurusDispositions,
      expandedDisposisiId,
      disposisiComments,
      commentsLoading,
      commentsStatusLoading,
      newCommentText,
      submittingComment,
      commentActiveTab,
      commentFileList,
      submitConfirmModalVisible,
      // Functions
      triggerFileInput,
      handleFileChange,
      handleFileDrop,
      handleDragOver,
      handleDragLeave,
      deleteFile,
      addTask,
      removeTask,
      addPengurusDisposition,
      removePengurusDisposition,
      handleSubmit,
      getFileName,
      getSuratTugasFileName,
      getFileUrl,
      openFilePreview,
      isSuratKeluarAttachment,
      getAttachmentName,
      navigateToSuratKeluar,
      openSuratTugasPreview,
      goBack,
      // New functions
      getDisposisiKepadaNames,
      formatDateTime,
      formatCommentTime,
      getStatusColor,
      getDisposisiSubstatus,
      getCommentsStatus,
      getCommentsStatusColor,
      isDisposisiCompleted,
      areAllDispositionsCompleted,
      isDisposisiCreator,
      isCommentOwner,
      canCommentOnDisposisi,
      beforeCommentUpload,
      handleCommentFileChange,
      removeCommentFile,
      toggleDisposisiComments,
      submitDisposisiComment,
      markDisposisiComplete,
      confirmMarkDisposisiComplete,
      cancelMarkComplete,
      confirmCompleteModalVisible,
      markingComplete,
      addNewTask,
      removeNewTask,
      addNewPengurusDisposition,
      removeNewPengurusDisposition,
      addPerwakilanDinas,
      removePerwakilanDinas,
      handlePreviewPerwakilan,
      addVerifikator,
      removeVerifikator,
      getPengurusJabatan,
      getPengurusNPA,
      getPengurusName,
      filterPengurusOption,
      incrementNomorSurat,
      decrementNomorSurat,
      confirmSubmit,
      displayCommentsStatus,
      // Surat Keluar Modal
      suratKeluarModalVisible,
      suratKeluarList,
      suratKeluarLoading,
      suratKeluarSearch,
      selectedSuratKeluar,
      filteredSuratKeluar,
      suratKeluarColumns,
      openSuratKeluarModal,
      onSuratKeluarSelectionChange,
      handleAddSelectedSuratKeluar,
      confirmSuratKeluarSelection,
      closeSuratKeluarModal,
      showScrollTop,
      scrollToTop,
      // PDF Preview Modal
      pdfPreviewVisible,
      pdfPreviewTitle,
      pdfPreviewPath,
      pdfPreviewList,
      // Auto-detect AI
      newFile,
      isDetecting,
      autoDetectWithAI,
      // Field disable state
      isFieldsDisabled,
    }
  },
}
</script>

<style lang="scss" scoped>
.update-surat-masuk-page {
  min-height: calc(100vh - 64px);
  padding: 24px;

  @media (max-width: 768px) {
    padding: 0;
  }

  .page-header {
    margin-bottom: 24px;

    .header-title-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #262626;
    }
  }

  .loading-container,
  .error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
    padding: 48px 24px;
  }

  .content-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 16px;
    }

    .upload-area {
      border: 2px dashed #d9d9d9;
      border-radius: 8px;
      padding: 48px 24px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s;

      &:hover,
      &.drag-over {
        border-color: #e10f28;
        background: #fdf4f5;
      }

      .upload-icon {
        margin-bottom: 16px;
        display: flex;
        justify-content: center;
      }

      .upload-text {
        font-size: 16px;
        color: #262626;
        margin-bottom: 8px;
      }

      .upload-hint {
        font-size: 14px;
        color: #8c8c8c;
        margin: 0;
      }
    }

    .file-attachment {
      display: flex;
      align-items: center;
      padding: 4px 12px;
      background: white;
      border-radius: 4px;
      gap: 8px;

      &:hover {
        background: #fafafa;
      }

      &:last-child {
        margin-bottom: 0;
      }

      .file-icon {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.45);
      }

      .file-name {
        flex: 1;
        font-size: 14px;
        color: #321e8d;
        word-break: break-word;
        text-decoration: none;
        transition: color 0.2s;

        &:hover {
          text-decoration: underline;
        }
      }

      .delete-button {
        background: none;
        border: none;
        cursor: pointer;
        color: #ff4d4f;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 2px;
        transition: all 0.2s;

        &:hover {
          color: #ff7875;
          background: #fff1f0;
        }

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }

    .section-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #282828;
    }

    :deep(.ant-radio-wrapper) {
      display: block;
      margin-bottom: 12px;
      font-size: 14px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .task-cards-section {
      margin-top: 16px;

      .task-info-text {
        color: #8c8c8c;
        font-size: 13px;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .task-card {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;

        @media (max-width: 768px) {
          padding: 12px;
        }
      }

      .tambah-task-button {
        color: #262626;
        margin-bottom: 16px;
        font-weight: 600;
        font-size: 14px;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }

    .pengurus-cards-section {
      .pengurus-info-text {
        color: #8c8c8c;
        font-size: 13px;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .pengurus-card {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;
      }

      .hapus-disposisi-button {
        background: #1a1a1a;
        border: 2px solid #1a1a1a;
        border-radius: 50px;
        padding: 6px 12px;
        margin-top: 4px;
        font-size: 14px;
        font-weight: 600;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.3s;

        svg {
          flex-shrink: 0;

          path {
            transition: stroke 0.3s;
          }
        }

        &:hover {
          background: white;
          border-color: #1a1a1a;
          color: #1a1a1a;

          svg path {
            stroke: #1a1a1a;
          }
        }
      }

      .tambah-disposisi-button {
        color: #262626;
        font-weight: 700;
        margin-bottom: 16px;
        font-size: 14px;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }

    // Surat Tugas List Styles for Penugasan Dinas
    .surat-tugas-list {
      margin-bottom: 16px;
      border-radius: 8px;
      overflow-x: auto;
      overflow-y: hidden;
      display: flex;
      flex-direction: column;

      &::-webkit-scrollbar {
        height: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;

        &:hover {
          background: #bfbfbf;
        }
      }

      .surat-tugas-info {
        font-size: 14px;
        margin-bottom: 12px;
        flex-shrink: 0;
      }

      .surat-tugas-items-container {
        display: flex;
        flex-direction: column;
        overflow-x: auto;
        overflow-y: hidden;

        &::-webkit-scrollbar {
          height: 6px;
        }

        &::-webkit-scrollbar-track {
          background: #f0f0f0;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
          background: #d9d9d9;
          border-radius: 3px;

          &:hover {
            background: #bfbfbf;
          }
        }
      }

      .surat-tugas-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0;
        white-space: nowrap;
        flex-shrink: 0;

        .surat-tugas-icon {
          color: #321e8d;
          font-size: 14px;
          flex-shrink: 0;
        }

        .surat-tugas-name {
          color: #321e8d;
          font-size: 14px;
          font-weight: 400;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 200px;
        }

        .surat-tugas-action {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          color: #e10f28;
          transition: all 0.2s;
          border-radius: 4px;
          flex-shrink: 0;
          cursor: pointer;

          &:hover {
            background: #fff1f0;
          }

          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    // New disposisi cards styles
    .disposisi-info-text {
      color: #8c8c8c;
      font-size: 13px;
      margin-bottom: 16px;
      line-height: 1.6;
    }

    .disposisi-cards-section {
      margin-top: 16px;

      .disposisi-item-card {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
        background: white;

        .disposisi-item-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;

          @media (max-width: 768px) {
            flex-direction: column;
            gap: 12px;
          }

          .disposisi-item-left {
            flex: 1;

            .disposisi-item-title {
              font-weight: 600;
              font-size: 14px;
              color: #262626;
              margin-bottom: 4px;
            }

            .disposisi-item-subtitle {
              font-size: 14px;
              color: #444;
              margin-bottom: 4px;
              font-weight: 500;
            }

            .disposisi-item-instruksi {
              font-size: 14px;
              color: #444;
              margin-top: 8px;
            }
          }

          .disposisi-item-right {
            text-align: right;

            @media (max-width: 768px) {
              text-align: left;
            }

            .disposisi-item-info {
              font-size: 14px;
              color: #444;
            }
          }
        }

        .disposisi-item-actions {
          display: flex;
          align-items: center;
          gap: 8px;

          @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
          }

          .lihat-komentar-btn {
            border: 2px solid #e10f28;
            color: #e10f28;
            border-radius: 50px;
            font-weight: 700;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
            gap: 6px;

            @media (max-width: 480px) {
              width: 100%;
              justify-content: center;
            }

            &.active,
            &:hover {
              background: #fff1f0;
            }

            .anticon {
              transition: transform 0.3s;
            }
          }
        }

        .disposisi-comments-section {
          margin-top: 16px;
          border-top: 1px dashed #e8e8e8;
          padding-top: 16px;

          .comments-list {
            margin-bottom: 24px;
            height: 400px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 16px;

            .comment-item {
              display: flex;
              flex-direction: column;
              align-items: flex-start;

              &.own-comment {
                align-items: flex-end;

                .comment-bubble {
                  background: #f2f4f8;
                }
              }

              .comment-bubble {
                background: #efefff;
                border-radius: 12px;
                padding: 12px 16px;
                width: 90%;

                .comment-time {
                  font-size: 12px;
                  color: #8c8c8c;
                  margin-top: 4px;
                  padding: 0 4px;
                }

                .comment-user-name {
                  font-weight: 600;
                  font-size: 13px;
                  color: #262626;
                  margin-bottom: 8px;
                  display: flex;
                  justify-content: space-between;
                  @media (max-width: 430px) {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 4px;
                  }

                  .comment-time {
                    font-size: 12px;
                    color: #798892;
                    margin-top: 4px;
                    padding: 0 4px;
                    font-weight: 400;

                    @media (max-width: 430px) {
                      padding: 0;
                    }
                  }
                }

                .comment-content {
                  p {
                    margin: 0;
                    color: #262626;
                    font-size: 14px;
                    line-height: 1.5;
                  }
                }
              }
            }
          }

          .comment-input-section {
            border-radius: 8px;
            overflow: hidden;

            .comment-action-button {
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 8px 12px;
              background: transparent;
              border: 2px solid transparent;
              border-radius: 8px;
              cursor: pointer;
              font-size: 12px;
              font-weight: 700;
              color: #282828;
              transition: all 0.2s ease;

              &:hover {
                border-color: #d9d9d9;
                color: #262626;
              }
            }

            .comment-action-button:disabled {
              color: #98a2b3;

              &:hover {
                border-color: transparent;
                cursor: not-allowed;
              }
            }

            .upload-button-wrapper {
              display: inline-block;
            }

            .attachment-preview {
              border-radius: 6px;
              margin-bottom: 16px;

              .attachment-item {
                display: flex;
                align-items: center;
                padding: 4px 12px;
                background: white;
                border-radius: 4px;
                width: 50%;
                gap: 8px;
                margin-bottom: 4px;

                &:hover {
                  background: #fafafa;
                }

                &:last-child {
                  margin-bottom: 0;
                }

                .file-icon {
                  font-size: 14px;
                  color: rgba(0, 0, 0, 0.45);
                }

                .attachment-name {
                  flex: 1;
                  font-size: 14px;
                  color: #321e8d;
                  word-break: break-word;
                }

                .delete-button {
                  background: none;
                  border: none;
                  cursor: pointer;
                  color: #ff4d4f;
                  padding: 4px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 2px;
                  transition: all 0.2s;

                  &:hover {
                    color: #ff7875;
                    background: #fff1f0;
                  }

                  svg {
                    width: 16px;
                    height: 16px;
                  }
                }
              }
            }

            .comment-tabs {
              display: flex;
              gap: 8px;
              padding: 12px 0;
              border-top: 1px solid #d9d9d9;
              align-items: center;
              flex-wrap: wrap;
            }

            .comment-textarea-wrapper {
              padding: 0;
              .custom-comment-input-row {
                display: flex;
                align-items: flex-end;
                gap: 12px;
                width: 100%;

                @media (max-width: 768px) {
                  flex-direction: column;
                  align-items: stretch;
                }
              }
              .custom-comment-textarea {
                border: 1px solid #d9d9d9;
                border-radius: 10px;
                resize: none;
                min-height: 48px;
                font-size: 14px;
                flex: 1 1 auto;
                transition: box-shadow 0.2s;
                box-shadow: none;
                padding: 12px 16px;
              }
              .custom-send-btn {
                background: #e10f28;
                border: none;
                color: #fff;
                border-radius: 50px;
                min-width: 80px;
                height: 36px;
                font-weight: 600;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                transition: background 0.2s;
                box-shadow: none;
              }
              .custom-send-btn:disabled {
                background: #d9d9d9;
                color: #fff;
              }
              .custom-send-btn:not(:disabled):hover {
                background: #f3374d;
                color: #fff;
              }
              .send-label {
                font-size: 13px;
                font-weight: 600;
                letter-spacing: 0.5px;
                margin: 0;
              }
            }

            .mark-complete-btn {
              margin-top: 12px;
              border: 2px solid #e10f28;
              color: #e10f28;
              border-radius: 50px;
              font-weight: 600;

              &:not(:disabled):hover {
                background: #fff1f0;
              }

              &:disabled {
                border: 2px solid #d9d9d9;
                color: #d9d9d9;
                background: white;
              }
            }
          }
        }
      }

      .task-card,
      .pengurus-card {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;
      }

      .tambah-task-button,
      .tambah-disposisi-button {
        color: #262626;
        margin-bottom: 16px;
        font-weight: 600;
        font-size: 14px;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }
  }
}

.no-comments {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding: 85px 48px;
  color: #798892;
}

.comment-attachment,
.comment-surat-keluar {
  margin-bottom: 8px;

  .attachment-link,
  .surat-keluar-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #321e8d;
    font-size: 14px;
    text-decoration: none;

    .export-icon {
      color: #e10f28;
    }

    .attachment-icon {
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .view-icon-link {
    cursor: pointer;
    display: inline-flex;
    align-items: center;

    &:hover {
      opacity: 0.8;
    }
  }
}

.remove-cancel-button {
  border-radius: 50px;
  padding: 6px 24px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  svg {
    flex-shrink: 0;

    path {
      transition: stroke 0.3s;
    }
  }
}

.remove-cancel-primary {
  background: #1a1a1a;
  border: 2px solid #1a1a1a;
  color: white;

  &:hover {
    background: white;
    border-color: #1a1a1a;
    color: #1a1a1a;

    svg path {
      stroke: #1a1a1a;
    }
  }
}

.remove-cancel-secondary {
  background: #ffffff;
  border: 2px solid #1a1a1a;
  color: #1a1a1a;

  &:hover {
    background: #1a1a1a;
    border-color: white;
    color: white;

    svg path {
      stroke: white;
    }
  }
}

.confirm-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  width: 50%;
  max-width: 200px;
  background: #e10f28;
  color: white;
  padding: 6px 12px;
  transition: all 0.3s;
  height: auto;
  line-height: 1.5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    background: white;
    border-color: #e10f28;
    color: #e10f28;
  }

  &:disabled {
    border-color: #98a2b3;
    background: #98a2b3;
    color: white;
    cursor: not-allowed;
    opacity: 0.8;
  }

  .button-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #ffffff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

.hapus-disposisi-button,
.hapus-task-disposisi {
  background: #1a1a1a;
  border: 2px solid #1a1a1a;
  border-radius: 50px;
  padding: 6px 18px;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  svg {
    flex-shrink: 0;

    path {
      transition: stroke 0.3s;
    }
  }

  &:hover {
    background: white;
    border-color: #1a1a1a;
    color: #1a1a1a;

    svg path {
      stroke: #1a1a1a;
    }
  }
}

.view-icon {
  color: #e10f28;
}

:deep(label.ant-form-item-required) {
  font-weight: 600 !important;
}

::v-deep(.ant-radio-group) {
  display: flex;

  @media (max-width: 714px) {
    flex-direction: column;
  }
}

:deep(.ant-input::placeholder),
:deep(.ant-input-number::placeholder),
:deep(.ant-picker::placeholder),
:deep(.ant-select-selector::placeholder),
:deep(.ant-select-selection-placeholder),
:deep(.ant-textarea::placeholder) {
  color: #bfbfbf !important;
  opacity: 1 !important;
  font-size: 14px;
}

:deep(.ant-input[disabled]),
:deep(.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector) {
  color: #798892 !important;
  font-size: 14px !important;
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}

// Confirmation Modal Styles
.confirm-complete-modal {
  .confirm-modal-content {
    .confirm-modal-title {
      font-size: 20px;
      font-weight: 700;
      color: #062d4b;
      margin-bottom: 16px;
    }

    .confirm-modal-description {
      font-size: 14px;
      color: #062d4b;
      margin-bottom: 24px;
    }

    .confirm-modal-actions {
      display: flex;
      justify-content: center;
      gap: 12px;

      .btn-kembali {
        min-width: 120px;
        height: 40px;
        border: 1px solid #d9d9d9;
        color: #595959;
        font-weight: 600;
        border-radius: 6px;

        &:hover {
          border-color: #e10f28;
          color: #e10f28;
        }
      }

      .btn-confirm-selesai {
        min-width: 180px;
        height: 40px;
        background-color: #e10f28;
        border-color: #e10f28;
        font-weight: 600;
        border-radius: 6px;

        &:hover {
          background-color: transparent;
          border-color: #e10f28;
          color: #e10f28;
        }
      }
    }
  }
}

// Surat Keluar Modal Styles
.surat-keluar-modal {
  .modal-subtitle {
    font-size: 14px;
    color: #595959;
    margin-bottom: 16px;
  }

  .surat-keluar-search {
    margin-bottom: 16px;

    :deep(.ant-input) {
      border-radius: 8px;
    }
  }

  .surat-keluar-table-container {
    max-height: 450px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    margin-bottom: 16px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;

    @media (max-width: 768px) {
      overflow-x: auto;
    }

    .surat-nomor {
      font-weight: 600;
      color: #321e8d;
    }

    :deep(.ant-spin-container) {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    :deep(.ant-table-wrapper) {
      display: flex;
      flex-direction: column;

      // Fix header and body alignment
      .ant-table {
        font-size: 13px;
        flex: 1;
        min-width: 900px;

        @media (max-width: 768px) {
          min-width: 1100px;
        }
      }

      // Ensure body scrolls and header stays fixed
      .ant-table-head {
        overflow: hidden !important;

        th {
          padding: 8px 12px;
          font-weight: 600;
          background: #fafafa;
        }
      }

      .ant-table-body {
        overflow-x: auto !important;
        overflow-y: auto !important;

        td {
          padding: 8px 12px;
        }
      }
    }
  }

  .surat-keluar-modal-actions {
    display: flex;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}

.loading-modal {
  text-align: center;
  padding: 40px 60px;

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #f0f0f0;
    border-top: 4px solid #e10f28;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 24px;
  }

  .loading-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 8px 0;
  }

  .loading-text {
    font-size: 14px;
    color: #8c8c8c;
    margin: 0 auto;
    max-width: 300px;
    line-height: 1.6;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Scroll to Top Button
.scroll-to-top-btn {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #7c3aed;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    background: #6d28d9;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
}

// Fade transition for scroll button
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.info-icon {
  margin-left: 8px;
  color: #8c8c8c;
  cursor: default;
}

::v-deep(.ant-checkbox-inner) {
  border-radius: 4px !important;
}

::v-deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #e10f28 !important;
  border-color: #e10f28 !important;
}

::v-deep(.ant-checkbox-indeterminate .ant-checkbox-inner::after) {
  background-color: transparent !important;
}

::v-deep(.ant-checkbox-indeterminate .ant-checkbox-inner) {
  background-color: transparent !important;
  border-color: #d9d9d9 !important;
}

// Auto-detect button styles
.auto-detect-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  .auto-detect-button {
    background: white;
    border: 2px solid #e10f28;
    color: #e10f28;
    border-radius: 50px;
    padding: 8px 24px;
    height: auto;
    font-weight: 600;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s;

    svg {
      flex-shrink: 0;
    }

    &:hover:not(:disabled) {
      background: #fff1f0;
      border-color: #e10f28;
      color: #e10f28;
    }

    &:disabled {
      border-color: #d9d9d9;
      color: #d9d9d9;
      cursor: not-allowed;
    }
  }

  .auto-detect-info {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #f5f5f5;
    cursor: help;
    transition: background 0.2s;

    &:hover {
      background: #e8e8e8;
    }

    svg {
      color: #8c8c8c;
    }
  }
}

.comment-tags-small {
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 2px;
    margin-bottom: 8px;
  }
}

.comment-tags-big {
  display: flex;
  gap: 2px;

  @media (max-width: 768px) {
    display: none;
  }
}

// Penugasan Dinas Section Styles
.penugasan-dinas-section {
  border-radius: 8px;

  .penugasan-info-text {
    color: #8c8c8c;
    font-size: 13px;
    margin-bottom: 24px;
    line-height: 1.6;
  }

  .bentuk-surat-option {
    margin-bottom: 16px;

    .bentuk-surat-desc {
      color: #8c8c8c;
      font-size: 12px;
      margin: 8px 0 0 24px;
      line-height: 1.5;
    }
  }

  // Nomor Surat Input Styles
  .nomor-surat-input {
    margin-bottom: 24px;

    .section-label {
      font-weight: 600;
      color: #262626;
      margin-bottom: 8px;
      display: block;
    }

    .section-description {
      color: #8c8c8c;
      font-size: 13px;
      margin-bottom: 12px;
      line-height: 1.6;
    }

    .input-wrapper {
      display: flex;
      gap: 8px;
      align-items: center;

      .nomor-input {
        flex: 1;
      }

      .btn-action {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid white;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover:not(:disabled) {
          border-color: #e10f28;
          color: #e10f28;
        }

        &:disabled {
          color: #d9d9d9;
          cursor: not-allowed;
          border-color: #f0f0f0;
          background: #fafafa;
        }
      }
    }

    .error-hint {
      color: #ff4d4f;
      font-size: 14px;
      margin-top: 4px;
      display: block;
    }
  }
}

:deep(.ant-form-item-label > label) {
  font-weight: 600;
  color: #262626;
}

:deep(.ant-select-selection-item) {
  font-size: 14px !important;
}

/* 1. The border color when selected */
:deep(.ant-radio-checked .ant-radio-inner) {
  border-color: #e10f28 !important;
}

/* 2. The inner dot color when selected */
:deep(.ant-radio-checked .ant-radio-inner::after) {
  background-color: #e10f28 !important;
}

/* 3. The border color on hover (only for enabled radios) */
:deep(.ant-radio-wrapper:not(.ant-radio-wrapper-disabled):hover .ant-radio .ant-radio-inner),
:deep(.ant-radio:not(.ant-radio-disabled):hover .ant-radio-inner) {
  border-color: #e10f28 !important;
}

/* 4. (Optional) The focus glow ring/shadow */
:deep(.ant-radio-input:focus + .ant-radio-inner) {
  box-shadow: 0 0 0 3px rgba(225, 15, 40, 0.2);
}

/* 5. Disabled radio border */
:deep(.ant-radio-disabled .ant-radio-inner) {
  border-color: #d9d9d9 !important;
  background-color: white !important;
}

/* 6. Disabled radio inner dot */
:deep(.ant-radio-disabled .ant-radio-inner::after) {
  background-color: #bfbfbf !important;
}

/* 7. Disabled hover (prevent red on hover) - Higher specificity */
:deep(.ant-radio-wrapper-disabled:hover .ant-radio .ant-radio-inner),
:deep(.ant-radio-wrapper-disabled .ant-radio:hover .ant-radio-inner),
:deep(.ant-radio-disabled:hover .ant-radio-inner) {
  border-color: #d9d9d9 !important;
}

/* 8. Disabled label text */
:deep(.ant-radio-wrapper-disabled span) {
  color: #bfbfbf !important;
  cursor: not-allowed;
}
</style>
